<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useEventListener, useResizeObserver } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import {
  readPinnedExecution,
  clearPinnedExecution,
} from './workflowEditorPinnedExecution';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import FlowNodeConfigPanel from './FlowNodeConfigPanel.vue';
import {
  normalizeMessageFilters,
  MESSAGE_TYPE_OPTIONS,
  CONTENT_MATCH_OPERATOR_OPTIONS,
} from './messageTriggerFilterConfig';
import { normalizeConversationFilters } from './conversationTriggerFilterConfig';
import {
  normalizeWorkflowConversationStatus,
  normalizeWorkflowConversationPriority,
  normalizeWorkflowWaitMinutes,
  normalizeWorkflowHttpRequestFields,
  normalizeWorkflowAiAgentFields,
  normalizeWorkflowConversationCustomAttributesFields,
  summarizeWorkflowWaitActionMinutes,
  summarizeWorkflowHttpRequest,
  summarizeWorkflowConversationCustomAttributes,
  workflowStatusLabel,
  workflowPriorityLabel,
} from './workflowConversationActionOptions';
import {
  normalizeWorkflowConditionData,
  conditionAttributeLabel,
  conditionOperatorLabel,
} from './workflowConditionNodeConfig';
import {
  normalizeKanbanWorkflowTriggerFields,
  normalizeKanbanCreateItemFields,
  normalizeKanbanMoveToStageFields,
  normalizeKanbanAssignAgentFields,
  normalizeKanbanRemoveAgentFields,
  normalizeKanbanAddNoteFields,
  normalizeKanbanChangeItemStatusFields,
  summarizeKanbanWorkflowTrigger,
  summarizeKanbanWorkflowAction,
} from './workflowKanbanActionOptions';

const NODE_W = 200;
const CONDITION_NODE_W = 288;
/** Do topo do nó até o topo da primeira linha de regra (antes de `divide-y`; ~header+rótulo+border-t). */
const CONDITION_BEFORE_RULE_ROWS = 70;
/** Altura exterior de cada linha (= `min-h-9`). */
const CONDITION_RULE_ROW_H = 36;
/** `divide-y` entre linhas de regra. */
const CONDITION_RULE_DIVIDER_W = 1;
/** Centro vertical da primeira bolinha (= topo da 1ª linha + metade da altura da linha). */
const CONDITION_FIRST_RULE_CENTER_OFFSET =
  CONDITION_BEFORE_RULE_ROWS + CONDITION_RULE_ROW_H / 2;
/** Distância entre centros de saídas consecutivas (linha + borda inferior da anterior). */
const CONDITION_RULE_CENTER_STEP =
  CONDITION_RULE_ROW_H + CONDITION_RULE_DIVIDER_W;
const NODE_H = 96;
/** Tamanho do nó circular «Agente de IA» (largura = altura). */
const AI_AGENT_NODE_SIZE = 152;
const NODE_SUMMARY_LINE_H = 16;
/** Tailwind `border` no quadro dos nós = 1px; alinha arestas SVG ao mesmo eixo das bolinhas (`left-0` / `right-0` + ±translate 50%). */
const NODE_FRAME_BORDER_W = 1;

/** Payload ao arrastar gatilho/ação do painel para o quadro (HTML5 DnD). */
const FLOW_PALETTE_DRAG_MIME = 'application/x-chatwoot-flow-palette-node';
const FLOW_PALETTE_TEXT_PREFIX = 'chatwoot-flow-palette:';
const CLEAR_PINNED_EXECUTION_TITLE = 'Limpar execução fixada';

const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;
const ZOOM_STEP = 1.12;
/** Evita scale(1) + translate fracionário, que desfocam texto/bordas no Chrome. */
const ZOOM_SNAP_TO_ONE = 0.002;

const TRIGGER_KINDS = [
  'message_created',
  'contact_created',
  'conversation_created',
  'label_added',
  'kanban_item_created',
  'kanban_item_updated',
  'kanban_item_stage_changed',
  'kanban_item_deleted',
];

const ACTION_KINDS = [
  'condition',
  'send_message',
  'add_private_note',
  'add_label',
  'remove_label',
  'assign_agent',
  'assign_team',
  'remove_agent',
  'remove_team',
  'set_custom_attributes',
  'transfer_inbox',
  'change_status',
  'change_priority',
  'wait',
  'http_request',
  'ai_agent',
  'kanban_create_item',
  'kanban_move_to_stage',
  'kanban_assign_agent',
  'kanban_remove_agent',
  'kanban_add_note',
  'kanban_change_item_status',
];

/** Cores hex (gatilhos na lista lateral); fundo em toda a linha, #RRGGBBAA. */
const TRIGGER_SIDEBAR_HEX = {
  message_created: {
    icon: '#F59E0B',
    idleBg: '#F59E0B24',
    hoverBg: '#F59E0B3D',
    activeBg: '#F59E0B5C',
  },
  contact_created: {
    icon: '#8B5CF6',
    idleBg: '#8B5CF624',
    hoverBg: '#8B5CF63D',
    activeBg: '#8B5CF65C',
  },
  conversation_created: {
    icon: '#0EA5E9',
    idleBg: '#0EA5E924',
    hoverBg: '#0EA5E93D',
    activeBg: '#0EA5E95C',
  },
  label_added: {
    icon: '#F43F5E',
    idleBg: '#F43F5E24',
    hoverBg: '#F43F5E3D',
    activeBg: '#F43F5E5C',
  },
  kanban_item_created: {
    icon: '#059669',
    idleBg: '#05966924',
    hoverBg: '#0596693D',
    activeBg: '#0596695C',
  },
  kanban_item_updated: {
    icon: '#047857',
    idleBg: '#04785724',
    hoverBg: '#0478573D',
    activeBg: '#0478575C',
  },
  kanban_item_stage_changed: {
    icon: '#0D9488',
    idleBg: '#0D948824',
    hoverBg: '#0D94883D',
    activeBg: '#0D94885C',
  },
  kanban_item_deleted: {
    icon: '#BE123C',
    idleBg: '#BE123C24',
    hoverBg: '#BE123C3D',
    activeBg: '#BE123C5C',
  },
};

const triggerSidebarHex = kind =>
  TRIGGER_SIDEBAR_HEX[kind] || TRIGGER_SIDEBAR_HEX.message_created;

const sidebarTriggerHoverKind = ref(null);
const sidebarTriggerActiveKind = ref(null);

const triggerSidebarRowStyle = kind => {
  const c = triggerSidebarHex(kind);
  let bg = c.idleBg;
  if (sidebarTriggerActiveKind.value === kind) {
    bg = c.activeBg;
  } else if (sidebarTriggerHoverKind.value === kind) {
    bg = c.hoverBg;
  }
  return {
    backgroundColor: bg,
  };
};

const triggerSidebarIconStyle = kind => ({
  color: triggerSidebarHex(kind).icon,
});

const triggerSidebarBorderLeftStyle = kind => ({
  borderLeftColor: triggerSidebarHex(kind).icon,
});

const triggerNodeHeaderStyle = node => {
  if (node.type !== 'trigger') return {};
  return { backgroundColor: triggerSidebarHex(node.data?.kind).idleBg };
};

const triggerNodeIconStyle = node => {
  if (node.type !== 'trigger') return {};
  return { color: triggerSidebarHex(node.data?.kind).icon };
};

const triggerNodeOutputHandleStyle = node => {
  if (node.type !== 'trigger') return {};
  return { borderColor: triggerSidebarHex(node.data?.kind).icon };
};

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { accountId } = useAccount();
const inboxesRaw = useMapGetter('inboxes/getInboxes');
const labelsRaw = useMapGetter('labels/getLabels');
const agentsRaw = useMapGetter('agents/getVerifiedAgents');
const teamsRaw = useMapGetter('teams/getTeams');
const funnelsRaw = useMapGetter('funnel/getFunnels');

const inboxesSorted = computed(() => {
  const list = inboxesRaw.value || [];
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const accountLabelTitle = id => {
  if (id === null || id === undefined) return '';
  const n = Number(id);
  if (!Number.isFinite(n)) return '';
  const list = labelsRaw.value || [];
  return list.find(l => l.id === n)?.title || '';
};

const accountAgentName = id => {
  if (id === null || id === undefined) return '';
  const n = Number(id);
  if (!Number.isFinite(n)) return '';
  const list = agentsRaw.value || [];
  return list.find(a => a.id === n)?.name || '';
};

const teamsSorted = computed(() => {
  const list = teamsRaw.value || [];
  return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const accountTeamName = id => {
  if (id === null || id === undefined) return '';
  const n = Number(id);
  if (!Number.isFinite(n)) return '';
  const list = teamsSorted.value || [];
  return list.find(team => team.id === n)?.name || '';
};

const funnelTitleById = id => {
  if (id === null || id === undefined || id === '') return '';
  const list = funnelsRaw.value || [];
  return list.find(f => String(f.id) === String(id))?.name || '';
};

const kanbanTriggerCardSummary = node => summarizeKanbanWorkflowTrigger(node);

const kanbanActionCardSummary = node =>
  summarizeKanbanWorkflowAction(node, funnelTitleById, accountAgentName);

const accountInboxName = id => {
  if (id === null || id === undefined) return '';
  const n = Number(id);
  if (!Number.isFinite(n)) return '';
  const list = inboxesSorted.value || [];
  return list.find(i => i.id === n)?.name || '';
};

const TRIGGER_LABELS = {
  message_created: 'Mensagem recebida',
  contact_created: 'Contato criado',
  conversation_created: 'Conversa criada',
  label_added: 'Etiqueta adicionada',
  kanban_item_created: 'Kanban: cartão criado',
  kanban_item_updated: 'Kanban: cartão atualizado',
  kanban_item_stage_changed: 'Kanban: mudou de etapa',
  kanban_item_deleted: 'Kanban: cartão excluído',
};

const ACTION_LABELS = {
  condition: 'Condição (SE)',
  send_message: 'Enviar mensagem',
  add_private_note: 'Nota privada',
  add_label: 'Adicionar etiqueta',
  remove_label: 'Remover etiqueta',
  assign_agent: 'Atribuir agente',
  assign_team: 'Atribuir time',
  remove_agent: 'Remover agente',
  remove_team: 'Remover time',
  set_custom_attributes: 'Atributos da conversa',
  transfer_inbox: 'Transferir para inbox',
  change_status: 'Alterar status',
  change_priority: 'Alterar prioridade',
  wait: 'Esperar',
  http_request: 'HTTP (API)',
  ai_agent: 'Agente de IA',
  kanban_create_item: 'Kanban: criar cartão',
  kanban_move_to_stage: 'Kanban: mover para etapa',
  kanban_assign_agent: 'Kanban: atribuir agente',
  kanban_remove_agent: 'Kanban: remover agente',
  kanban_add_note: 'Kanban: adicionar nota',
  kanban_change_item_status: 'Kanban: alterar status',
};

const flowActionKindLucideIcon = kind => {
  switch (kind) {
    case 'condition':
      return 'i-lucide-git-branch';
    case 'wait':
      return 'i-lucide-clock';
    case 'http_request':
      return 'i-lucide-globe';
    case 'ai_agent':
      return 'i-lucide-bot';
    case 'assign_team':
      return 'i-lucide-users';
    case 'remove_agent':
      return 'i-lucide-user-minus';
    case 'remove_team':
      return 'i-lucide-users';
    case 'set_custom_attributes':
      return 'i-lucide-list-tree';
    case 'kanban_create_item':
    case 'kanban_move_to_stage':
      return 'i-lucide-columns-3';
    case 'kanban_assign_agent':
      return 'i-lucide-user-plus';
    case 'kanban_remove_agent':
      return 'i-lucide-user-minus';
    case 'kanban_add_note':
      return 'i-lucide-sticky-note';
    case 'kanban_change_item_status':
      return 'i-lucide-flag';
    default:
      return 'i-lucide-play';
  }
};

/** Ícone + cor por tipo — usado nos cards do canvas (mantém legenda por ação). */
const flowActionKindIconClass = kind => {
  const icon = flowActionKindLucideIcon(kind);
  switch (kind) {
    case 'condition':
      return `${icon} text-violet-500`;
    case 'wait':
      return `${icon} text-sky-500`;
    case 'http_request':
      return `${icon} text-indigo-500`;
    case 'ai_agent':
      return `${icon} text-fuchsia-600`;
    case 'assign_team':
      return `${icon} text-cyan-600`;
    case 'remove_agent':
      return `${icon} text-rose-600`;
    case 'remove_team':
      return `${icon} text-rose-600`;
    case 'set_custom_attributes':
      return `${icon} text-violet-600`;
    case 'kanban_create_item':
    case 'kanban_move_to_stage':
      return `${icon} text-emerald-600`;
    case 'kanban_assign_agent':
      return `${icon} text-emerald-600`;
    case 'kanban_remove_agent':
      return `${icon} text-emerald-700`;
    case 'kanban_add_note':
      return `${icon} text-emerald-600`;
    case 'kanban_change_item_status':
      return `${icon} text-emerald-600`;
    default:
      return `${icon} text-teal-500`;
  }
};

const workflowId = computed(() => route.params.workflowId);
const pinnedExecution = ref(null);

const refreshPinnedExecution = () => {
  if (!workflowId.value || !accountId.value) {
    pinnedExecution.value = null;
    return;
  }
  pinnedExecution.value = readPinnedExecution(
    accountId.value,
    workflowId.value
  );
};

const onClearPinnedExecution = () => {
  clearPinnedExecution(accountId.value, workflowId.value);
  pinnedExecution.value = null;
};

/** Há dados de execução copiados do histórico fixados (localStorage) — mostrar pin nos nós. */
const pinnedExecutionPreviewActive = computed(() => {
  const p = pinnedExecution.value;
  if (!p || p.executionId == null) return false;
  const b = p.bindings;
  if (!b || typeof b !== 'object' || Array.isArray(b)) return false;
  return Object.keys(b).length > 0;
});

const pinnedExecutionPinTitle = computed(() => {
  const id = pinnedExecution.value?.executionId;
  if (id == null) return '';
  return `Valores da execução #${id} fixados no editor (pré-visualização no painel «Dados entre nós»)`;
});

const markerId = computed(
  () => `flow-arrow-${String(workflowId.value || 'new').replace(/\W/g, '')}`
);
const ghostMarkerId = computed(() => `${markerId.value}-ghost`);
const workflowName = ref('');
const workflowActive = ref(true);
const isUpdatingActive = ref(false);
const isLoading = ref(true);
const isSaving = ref(false);
const isDeleting = ref(false);
const isImporting = ref(false);
const importFileInputRef = ref(null);
const showFlowMoreMenu = ref(false);

const viewport = reactive({ x: 0, y: 0 });
const zoom = ref(1);
const graph = reactive({
  nodes: [],
  edges: [],
  sticky_notes: [],
});

const canvasRef = ref(null);
const isPanning = ref(false);

const clampZoom = z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

const zoomIsOne = computed(() => Math.abs(zoom.value - 1) < ZOOM_SNAP_TO_ONE);

const canvasLayerTransformStyle = computed(() => {
  if (zoomIsOne.value) {
    return {
      transform: `translate3d(${Math.round(viewport.x)}px, ${Math.round(viewport.y)}px, 0)`,
    };
  }
  return {
    transform: `translate3d(${viewport.x}px, ${viewport.y}px, 0) scale(${zoom.value})`,
  };
});

const setZoomAroundPoint = (nextZoomRaw, mx, my) => {
  let nextZ = clampZoom(nextZoomRaw);
  if (Math.abs(nextZ - 1) < ZOOM_SNAP_TO_ONE) {
    nextZ = 1;
  }
  const oldZ = zoom.value;
  const gx = (mx - viewport.x) / oldZ;
  const gy = (my - viewport.y) / oldZ;
  zoom.value = nextZ;
  viewport.x = mx - gx * nextZ;
  viewport.y = my - gy * nextZ;
  if (nextZ === 1) {
    viewport.x = Math.round(viewport.x);
    viewport.y = Math.round(viewport.y);
  }
};

const onCanvasWheel = e => {
  if (!(e.ctrlKey || e.metaKey)) return;
  e.preventDefault();
  const wrap = canvasRef.value;
  if (!wrap) return;
  const rect = wrap.getBoundingClientRect();
  const mx = e.clientX - rect.left;
  const my = e.clientY - rect.top;
  const factor = e.deltaY > 0 ? 1 / ZOOM_STEP : ZOOM_STEP;
  setZoomAroundPoint(zoom.value * factor, mx, my);
};

useEventListener(canvasRef, 'wheel', onCanvasWheel, { passive: false });

const canvasViewportCenter = () => {
  const wrap = canvasRef.value;
  if (!wrap) return { mx: 320, my: 240 };
  const r = wrap.getBoundingClientRect();
  return { mx: r.width / 2, my: r.height / 2 };
};

const zoomIn = () => {
  const { mx, my } = canvasViewportCenter();
  setZoomAroundPoint(zoom.value * ZOOM_STEP, mx, my);
};

const zoomOut = () => {
  const { mx, my } = canvasViewportCenter();
  setZoomAroundPoint(zoom.value / ZOOM_STEP, mx, my);
};

const resetZoom = () => {
  const { mx, my } = canvasViewportCenter();
  setZoomAroundPoint(1, mx, my);
};

const zoomPercentLabel = computed(() => `${Math.round(zoom.value * 100)}%`);

const zoomAtMin = computed(() => zoom.value <= ZOOM_MIN + 0.001);
const zoomAtMax = computed(() => zoom.value >= ZOOM_MAX - 0.001);

const snapViewportIfZoomOne = () => {
  if (!zoomIsOne.value) return;
  viewport.x = Math.round(viewport.x);
  viewport.y = Math.round(viewport.y);
};

const canvasSize = reactive({ w: 800, h: 480 });

useResizeObserver(canvasRef, entries => {
  const entry = entries[0];
  if (!entry?.contentRect) return;
  canvasSize.w = entry.contentRect.width;
  canvasSize.h = entry.contentRect.height;
});

let panStart = { x: 0, y: 0, vx: 0, vy: 0 };

const dragNode = ref(null);
const dragMovedDuring = ref(false);
let dragStart = { x: 0, y: 0, px: 0, py: 0 };

const selectedNodeId = ref(null);
const selectedNode = computed(() =>
  selectedNodeId.value ? nodeById(selectedNodeId.value) : null
);

/** Painel «Dados entre nós»: expandido por defeito; pode recolher para um botão no canvas. */
const workflowVariablesPanelExpanded = ref(true);

const closeNodePanel = () => {
  selectedNodeId.value = null;
};

const onEscapePanel = e => {
  if (e.key === 'Escape') closeNodePanel();
};

const STICKY_DIM = {
  defaultW: 192,
  defaultH: 120,
};

/** Sem teto/piso de produto: só inteiro positivo e fallback para tamanho inicial. */
const normalizeStickyDimensions = (w, h) => {
  const cw = Number(w);
  const ch = Number(h);
  const width = Number.isFinite(cw)
    ? Math.max(1, Math.round(cw))
    : STICKY_DIM.defaultW;
  const height = Number.isFinite(ch)
    ? Math.max(1, Math.round(ch))
    : STICKY_DIM.defaultH;
  return { width, height };
};

const normalizeStickyNote = raw => {
  const pos =
    raw?.position && typeof raw.position === 'object' ? raw.position : {};
  const id =
    raw?.id !== null && raw?.id !== undefined && String(raw.id).length
      ? String(raw.id)
      : crypto.randomUUID();
  const { width, height } = normalizeStickyDimensions(raw?.width, raw?.height);
  return {
    id,
    position: {
      x: Number(pos.x ?? 240),
      y: Number(pos.y ?? 160),
    },
    width,
    height,
    text: typeof raw?.text === 'string' ? raw.text : '',
  };
};

const normalizeGraph = raw => {
  const g = raw && typeof raw === 'object' ? raw : {};
  return {
    nodes: Array.isArray(g.nodes) ? g.nodes : [],
    edges: Array.isArray(g.edges) ? g.edges : [],
    sticky_notes: Array.isArray(g.sticky_notes)
      ? g.sticky_notes.map(normalizeStickyNote)
      : [],
  };
};

const normalizeNode = raw => {
  const d = raw.data && typeof raw.data === 'object' ? raw.data : {};
  const pos =
    raw.position && typeof raw.position === 'object' ? raw.position : {};
  const type = raw.type === 'trigger' ? 'trigger' : 'action';
  const data = {
    kind: d.kind,
    label: d.label,
  };
  if (type === 'trigger' && d.kind === 'message_created') {
    data.message_filters = normalizeMessageFilters(d.message_filters);
  }
  if (type === 'trigger' && d.kind === 'conversation_created') {
    data.conversation_filters = normalizeConversationFilters(
      d.conversation_filters
    );
  }
  if (type === 'trigger' && d.kind === 'label_added') {
    const lid = d.label_id;
    const n =
      lid === null || lid === undefined || lid === '' ? NaN : Number(lid);
    data.label_id = Number.isFinite(n) ? n : null;
  }
  if (
    type === 'trigger' &&
    [
      'kanban_item_created',
      'kanban_item_updated',
      'kanban_item_stage_changed',
      'kanban_item_deleted',
    ].includes(d.kind)
  ) {
    Object.assign(data, normalizeKanbanWorkflowTriggerFields(d.kind, d));
  }
  if (type === 'action' && d.kind === 'send_message') {
    data.message_content =
      typeof d.message_content === 'string' ? d.message_content : '';
  }
  if (type === 'action' && d.kind === 'add_private_note') {
    data.private_note_content =
      typeof d.private_note_content === 'string' ? d.private_note_content : '';
  }
  if (type === 'action' && d.kind === 'add_label') {
    const lid = d.label_id;
    const n =
      lid === null || lid === undefined || lid === '' ? NaN : Number(lid);
    data.label_id = Number.isFinite(n) ? n : null;
  }
  if (type === 'action' && d.kind === 'remove_label') {
    const lid = d.label_id;
    const n =
      lid === null || lid === undefined || lid === '' ? NaN : Number(lid);
    data.label_id = Number.isFinite(n) ? n : null;
  }
  if (type === 'action' && d.kind === 'assign_agent') {
    const aid = d.agent_id;
    const n =
      aid === null || aid === undefined || aid === '' ? NaN : Number(aid);
    data.agent_id = Number.isFinite(n) ? n : null;
  }
  if (
    type === 'action' &&
    (d.kind === 'assign_team' || d.kind === 'remove_team')
  ) {
    const tid = d.team_id;
    const n =
      tid === null || tid === undefined || tid === '' ? NaN : Number(tid);
    data.team_id = Number.isFinite(n) ? n : null;
  }
  if (type === 'action' && d.kind === 'set_custom_attributes') {
    Object.assign(data, normalizeWorkflowConversationCustomAttributesFields(d));
  }
  if (type === 'action' && d.kind === 'transfer_inbox') {
    const iid = d.inbox_id;
    const n =
      iid === null || iid === undefined || iid === '' ? NaN : Number(iid);
    data.inbox_id = Number.isFinite(n) ? n : null;
  }
  if (type === 'action' && d.kind === 'change_status') {
    data.status = normalizeWorkflowConversationStatus(d.status);
  }
  if (type === 'action' && d.kind === 'change_priority') {
    data.priority = normalizeWorkflowConversationPriority(d.priority);
  }
  if (type === 'action' && d.kind === 'wait') {
    data.wait_minutes = normalizeWorkflowWaitMinutes(d.wait_minutes);
  }
  if (type === 'action' && d.kind === 'http_request') {
    Object.assign(data, normalizeWorkflowHttpRequestFields(d));
  }
  if (type === 'action' && d.kind === 'ai_agent') {
    Object.assign(data, normalizeWorkflowAiAgentFields(d));
  }
  if (type === 'action' && d.kind === 'condition') {
    Object.assign(data, normalizeWorkflowConditionData(d));
  }
  if (type === 'action' && d.kind === 'kanban_create_item') {
    Object.assign(data, normalizeKanbanCreateItemFields(d));
  }
  if (type === 'action' && d.kind === 'kanban_move_to_stage') {
    Object.assign(data, normalizeKanbanMoveToStageFields(d));
  }
  if (type === 'action' && d.kind === 'kanban_assign_agent') {
    Object.assign(data, normalizeKanbanAssignAgentFields(d));
  }
  if (type === 'action' && d.kind === 'kanban_remove_agent') {
    Object.assign(data, normalizeKanbanRemoveAgentFields(d));
  }
  if (type === 'action' && d.kind === 'kanban_add_note') {
    Object.assign(data, normalizeKanbanAddNoteFields(d));
  }
  if (type === 'action' && d.kind === 'kanban_change_item_status') {
    Object.assign(data, normalizeKanbanChangeItemStatusFields(d));
  }
  return {
    id: String(raw.id),
    type,
    position: {
      x: Number(pos.x ?? 120),
      y: Number(pos.y ?? 120),
    },
    data,
  };
};

const normalizeSourceHandle = raw => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (raw === 'true' || raw === true) return 'true';
  if (raw === 'false' || raw === false) return 'false';
  if (typeof raw !== 'string') return null;
  return raw;
};

const normalizeEdge = raw => {
  const sh = raw.sourceHandle ?? raw.source_handle;
  const sourceHandle = normalizeSourceHandle(sh);
  const shSafe = sourceHandle ? String(sourceHandle).replace(/\|/g, '') : '';
  const idFallback = `${raw.source}-${raw.target}${shSafe ? `-${shSafe}` : ''}`;
  const base = {
    id: String(raw.id || idFallback),
    source: String(raw.source),
    target: String(raw.target),
  };
  if (sourceHandle) base.sourceHandle = sourceHandle;
  return base;
};

const isConditionActionNode = node =>
  node?.type === 'action' && node?.data?.kind === 'condition';

const isAiAgentActionNode = node =>
  node?.type === 'action' && node?.data?.kind === 'ai_agent';

const nodeWidth = node => {
  if (isAiAgentActionNode(node)) return AI_AGENT_NODE_SIZE;
  if (isConditionActionNode(node)) return CONDITION_NODE_W;
  return NODE_W;
};

const conditionOutputSlotHandles = node => {
  if (!isConditionActionNode(node)) return [];
  const rules = node.data?.condition_rules || [];
  return [...rules.map(r => r.id), 'else'];
};

const conditionOutputY = (node, sourceHandle) => {
  const handles = conditionOutputSlotHandles(node);
  const sh = sourceHandle || handles[0] || 'else';
  let idx = handles.indexOf(sh);
  if (idx < 0) idx = 0;
  return (
    node.position.y +
    CONDITION_FIRST_RULE_CENTER_OFFSET +
    idx * CONDITION_RULE_CENTER_STEP
  );
};

const conditionNodeHeight = node => {
  const handles = conditionOutputSlotHandles(node);
  const rows = Math.max(1, handles.length);
  const dividers = Math.max(0, rows - 1);
  return (
    CONDITION_BEFORE_RULE_ROWS +
    rows * CONDITION_RULE_ROW_H +
    dividers * CONDITION_RULE_DIVIDER_W +
    8
  );
};

const migrateLegacyConditionEdgesOn = (nodes, edges) => {
  const byId = new Map(nodes.map(n => [n.id, n]));
  edges.forEach(e => {
    if (!e.sourceHandle) return;
    const n = byId.get(e.source);
    if (!isConditionActionNode(n)) return;
    const rules = n.data?.condition_rules || [];
    if (!rules.length) return;
    if (e.sourceHandle === 'true') {
      e.sourceHandle = rules[0].id;
    } else if (e.sourceHandle === 'false') {
      e.sourceHandle = 'else';
    }
  });
};

const migrateLegacyConditionEdges = () => {
  migrateLegacyConditionEdgesOn(graph.nodes, graph.edges);
};

const onPruneEdgesFromHandle = ({ sourceNodeId, sourceHandle }) => {
  for (let i = graph.edges.length - 1; i >= 0; i -= 1) {
    const edge = graph.edges[i];
    if (edge.source === sourceNodeId && edge.sourceHandle === sourceHandle) {
      graph.edges.splice(i, 1);
    }
  }
};

const graphPayload = () => JSON.parse(JSON.stringify(graph));

const downloadFlowJson = () => {
  const payload = {
    name: workflowName.value,
    graph: graphPayload(),
  };
  const json = `${JSON.stringify(payload, null, 2)}\n`;
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const slug = (
    String(workflowName.value || '')
      .trim()
      .replace(/[^\w\-.]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'flow'
  ).slice(0, 80);
  const idPart = workflowId.value ? `-id-${workflowId.value}` : '';
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `conversation-workflow${idPart}-${slug}.json`;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const resolveImportGraphRaw = parsed => {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return null;
  if (
    parsed.graph !== null &&
    parsed.graph !== undefined &&
    typeof parsed.graph === 'object' &&
    !Array.isArray(parsed.graph)
  ) {
    return parsed.graph;
  }
  if (
    Object.prototype.hasOwnProperty.call(parsed, 'nodes') ||
    Object.prototype.hasOwnProperty.call(parsed, 'edges') ||
    Object.prototype.hasOwnProperty.call(parsed, 'sticky_notes')
  ) {
    return parsed;
  }
  return null;
};

const graphObjectForImport = rawGraph => {
  const n = normalizeGraph(rawGraph);
  const nodes = n.nodes.map(normalizeNode);
  const edges = n.edges.filter(e => e?.source && e?.target).map(normalizeEdge);
  migrateLegacyConditionEdgesOn(nodes, edges);
  return {
    nodes,
    edges,
    sticky_notes: n.sticky_notes,
  };
};

const openImportPicker = () => {
  importFileInputRef.value?.click();
};

const createWorkflowFromImport = async (name, graphObj) => {
  isImporting.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.create({
      conversation_workflow: {
        name,
        description: '',
        active: true,
        graph: graphObj,
      },
    });
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_SUCCESS'));
    const id = data?.payload?.id;
    if (id) {
      await router.push({
        name: 'conversation_workflows_edit',
        params: {
          accountId: accountId.value,
          workflowId: String(id),
        },
      });
    }
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_ERROR'));
  } finally {
    isImporting.value = false;
  }
};

const onImportFileChange = async e => {
  const input = e.target;
  const file = input.files?.[0];
  input.value = '';
  if (!file || isImporting.value) return;
  let text;
  try {
    text = await file.text();
  } catch {
    useAlert(t('CONVERSATION_WORKFLOW.FLOW_EDITOR.IMPORT_INVALID_JSON'));
    return;
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    useAlert(t('CONVERSATION_WORKFLOW.FLOW_EDITOR.IMPORT_INVALID_JSON'));
    return;
  }
  const graphRaw = resolveImportGraphRaw(parsed);
  if (!graphRaw) {
    useAlert(t('CONVERSATION_WORKFLOW.FLOW_EDITOR.IMPORT_INVALID_STRUCTURE'));
    return;
  }
  const nameHint =
    typeof parsed.name === 'string' && parsed.name.trim()
      ? parsed.name.trim()
      : null;
  const name = nameHint || t('FLOW_AUTOMATIONS.INDEX.DEFAULT_NAME');
  const graphObj = graphObjectForImport(graphRaw);
  await createWorkflowFromImport(name, graphObj);
};

const persistWorkflowActive = async () => {
  if (!workflowId.value || isLoading.value) return;
  const desired = workflowActive.value;
  isUpdatingActive.value = true;
  try {
    await conversationWorkflowsAPI.update(workflowId.value, {
      conversation_workflow: { active: desired },
    });
  } catch {
    workflowActive.value = !desired;
    useAlert(t('CONVERSATION_WORKFLOW.FLOW_EDITOR.ACTIVE_UPDATE_ERROR'));
  } finally {
    isUpdatingActive.value = false;
  }
};

const persist = async () => {
  if (!workflowId.value) return;
  isSaving.value = true;
  try {
    await conversationWorkflowsAPI.update(workflowId.value, {
      conversation_workflow: {
        name: workflowName.value,
        active: workflowActive.value,
        graph: graphPayload(),
      },
    });
    useAlert('Fluxo salvo.');
  } catch {
    useAlert('Não foi possível salvar o fluxo.');
  } finally {
    isSaving.value = false;
  }
};

const load = async () => {
  isLoading.value = true;
  let ok = false;
  try {
    const { data } = await conversationWorkflowsAPI.show(workflowId.value);
    const p = data?.payload ?? {};
    workflowName.value = p.name || '';
    workflowActive.value = p.active !== false && p.active !== 'false';
    const n = normalizeGraph(p.graph);
    graph.nodes.splice(0, graph.nodes.length, ...n.nodes.map(normalizeNode));
    graph.edges.splice(
      0,
      graph.edges.length,
      ...n.edges.filter(e => e?.source && e?.target).map(normalizeEdge)
    );
    migrateLegacyConditionEdges();
    graph.sticky_notes.splice(0, graph.sticky_notes.length, ...n.sticky_notes);
    closeNodePanel();
    stickyEditingId.value = null;
    ok = true;
  } catch {
    useAlert('Não foi possível carregar o fluxo.');
    router.push({
      name: 'conversation_workflows_index',
      params: { accountId: accountId.value },
    });
  } finally {
    isLoading.value = false;
  }
  return ok;
};

const goBack = () => {
  router.push({
    name: 'conversation_workflows_index',
    params: { accountId: accountId.value },
  });
};

const openExecutionsHistory = () => {
  if (!workflowId.value) return;
  router.push({
    name: 'conversation_workflows_executions',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
    },
  });
};

const deleteWorkflow = async () => {
  if (!workflowId.value) return;
  if (!window.confirm('Excluir este fluxo? Esta ação não pode ser desfeita.')) {
    return;
  }
  isDeleting.value = true;
  try {
    await conversationWorkflowsAPI.delete(workflowId.value);
    useAlert('Fluxo excluído.');
    goBack();
  } catch {
    useAlert('Não foi possível excluir o fluxo.');
  } finally {
    isDeleting.value = false;
  }
};

const flowMoreMenuItems = computed(() => {
  const busy =
    isLoading.value ||
    isSaving.value ||
    isDeleting.value ||
    isImporting.value;
  return [
    {
      label: t('CONVERSATION_WORKFLOW.FLOW_EDITOR.MENU_IMPORT_JSON'),
      action: 'importJson',
      value: 'importJson',
      icon: 'i-lucide-upload',
      disabled: busy,
    },
    {
      label: t('CONVERSATION_WORKFLOW.FLOW_EDITOR.MENU_EXPORT_JSON'),
      action: 'exportJson',
      value: 'exportJson',
      icon: 'i-lucide-download',
      disabled: busy,
    },
    {
      label: t('CONVERSATION_WORKFLOW.FLOW_EDITOR.MENU_DELETE'),
      action: 'delete',
      value: 'delete',
      icon: 'i-lucide-trash',
      disabled:
        isSaving.value ||
        isDeleting.value ||
        isImporting.value ||
        !workflowId.value,
    },
  ];
});

const handleFlowMoreMenuAction = ({ action }) => {
  showFlowMoreMenu.value = false;
  if (action === 'importJson') openImportPicker();
  else if (action === 'exportJson') downloadFlowJson();
  else if (action === 'delete') deleteWorkflow();
};

const nodeById = id => graph.nodes.find(n => n.id === id);

const messageTypeShortLabel = v =>
  MESSAGE_TYPE_OPTIONS.find(o => o.value === v)?.label || v;

const typeLabelBadgeClass =
  'shrink-0 rounded-md border border-n-slate-8 bg-n-slate-3 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-11';

const contentOpShortLabel = op =>
  CONTENT_MATCH_OPERATOR_OPTIONS.find(o => o.value === op)?.label || op;

const contentOperatorBadgeClass = op => {
  if (op === 'contains') {
    return 'border border-n-teal-8 bg-n-teal-4 text-n-teal-12';
  }
  if (op === 'equal_to') {
    return 'border border-n-amber-8 bg-n-amber-4 text-n-amber-12';
  }
  return 'border border-n-slate-7 bg-n-slate-4 text-n-slate-12';
};

/** Texto dos tipos (rótulos) após o badge "Tipo" no card. */
const triggerMessageCardTypeLabelsText = node => {
  if (node.type !== 'trigger' || node.data?.kind !== 'message_created')
    return '';
  const mf = node.data?.message_filters;
  if (!mf) return '';
  const labels = (mf.message_types || []).map(messageTypeShortLabel);
  return labels.length ? labels.join(' · ') : '';
};

/** Badge + trecho para filtro de conteúdo no card. */
const triggerMessageCardContentRow = node => {
  if (node.type !== 'trigger' || node.data?.kind !== 'message_created')
    return null;
  const mf = node.data?.message_filters;
  if (!mf) return null;
  const cop = mf.content_operator;
  const cv = String(mf.content_value || '').trim();
  if (!cop || cop === 'none' || !cv) return null;
  const snippet = cv.length > 32 ? `${cv.slice(0, 32)}…` : cv;
  return {
    operator: cop,
    opLabel: contentOpShortLabel(cop),
    snippet,
    badgeClass: contentOperatorBadgeClass(cop),
  };
};

const inboxIdsForTriggerCard = node => {
  if (node.type !== 'trigger') return [];
  if (node.data?.kind === 'message_created') {
    return node.data?.message_filters?.inbox_ids || [];
  }
  if (node.data?.kind === 'conversation_created') {
    return node.data?.conversation_filters?.inbox_ids || [];
  }
  return [];
};

const triggerInboxCardLabelsText = node => {
  const ids = inboxIdsForTriggerCard(node);
  if (!ids.length) return '';
  const map = new Map(inboxesSorted.value.map(i => [i.id, i.name]));
  return ids.map(id => map.get(id) || `#${id}`).join(' · ');
};

const triggerLabelAddedCardTitle = node => {
  if (node.type !== 'trigger' || node.data?.kind !== 'label_added') return '';
  const title = accountLabelTitle(node.data?.label_id);
  if (!title) return '';
  return title.length > 48 ? `${title.slice(0, 48)}…` : title;
};

const actionSendMessageCardSnippet = node => {
  if (node.type !== 'action' || node.data?.kind !== 'send_message') return '';
  const text = String(node.data?.message_content ?? '').trim();
  if (!text) return '';
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

const actionPrivateNoteCardSnippet = node => {
  if (node.type !== 'action' || node.data?.kind !== 'add_private_note')
    return '';
  const text = String(node.data?.private_note_content ?? '').trim();
  if (!text) return '';
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

const actionAddLabelCardTitle = node => {
  if (node.type !== 'action' || node.data?.kind !== 'add_label') return '';
  const title = accountLabelTitle(node.data?.label_id);
  if (!title) return '';
  return title.length > 48 ? `${title.slice(0, 48)}…` : title;
};

const actionRemoveLabelCardTitle = node => {
  if (node.type !== 'action' || node.data?.kind !== 'remove_label') return '';
  const title = accountLabelTitle(node.data?.label_id);
  if (!title) return '';
  return title.length > 48 ? `${title.slice(0, 48)}…` : title;
};

const actionAssignAgentCardName = node => {
  if (node.type !== 'action' || node.data?.kind !== 'assign_agent') return '';
  const name = accountAgentName(node.data?.agent_id);
  if (!name) return '';
  return name.length > 48 ? `${name.slice(0, 48)}…` : name;
};

const actionAssignTeamCardName = node => {
  if (node.type !== 'action' || node.data?.kind !== 'assign_team') return '';
  const name = accountTeamName(node.data?.team_id);
  if (!name) return '';
  return name.length > 48 ? `${name.slice(0, 48)}…` : name;
};

const actionRemoveAgentHint = node =>
  node.type === 'action' && node.data?.kind === 'remove_agent'
    ? 'Remove atribuição de agente'
    : '';

const actionRemoveTeamCardSummary = node => {
  if (node.type !== 'action' || node.data?.kind !== 'remove_team') return '';
  const tid = node.data?.team_id;
  if (tid === null || tid === undefined) {
    return 'Qualquer time · remove sempre';
  }
  const name = accountTeamName(tid);
  if (!name) return `Time #${tid}`;
  return name.length > 52 ? `${name.slice(0, 52)}…` : name;
};

const actionTransferInboxCardName = node => {
  if (node.type !== 'action' || node.data?.kind !== 'transfer_inbox') return '';
  const name = accountInboxName(node.data?.inbox_id);
  if (!name) return '';
  return name.length > 48 ? `${name.slice(0, 48)}…` : name;
};

const actionChangeStatusCardLabel = node => {
  if (node.type !== 'action' || node.data?.kind !== 'change_status') return '';
  const s = node.data?.status;
  if (!s) return '';
  const text = workflowStatusLabel(s);
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

const actionChangePriorityCardLabel = node => {
  if (node.type !== 'action' || node.data?.kind !== 'change_priority')
    return '';
  const p = node.data?.priority;
  if (!p) return '';
  const text = workflowPriorityLabel(p);
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
};

const conditionRuleSummaryLine = rule => {
  if (!rule) return '';
  const attr = rule.condition_attribute;
  const op = rule.condition_operator;
  const val = rule.condition_value;
  const inboxIds = rule.condition_inbox_ids || [];
  const attrShort = conditionAttributeLabel(attr)
    .replace(/\s+da\s+conversa|\s+de\s+entrada|\s+atribu[ií]do/gi, '')
    .trim();
  const withAttr = (...parts) =>
    `${attrShort} ${parts.filter(Boolean).join(' ')}`.trim();
  if (op === 'is_unassigned') {
    return `${attrShort}: ${conditionOperatorLabel(attr, op)}`;
  }
  if (attr === 'priority' && op === 'is_empty') {
    return `${attrShort}: ${conditionOperatorLabel(attr, op)}`;
  }
  if (attr === 'inbox' && (op === 'in' || op === 'not_in')) {
    if (!inboxIds.length)
      return `${attrShort} · ${conditionOperatorLabel(attr, op)}`;
    const map = new Map(inboxesSorted.value.map(i => [i.id, i.name]));
    const names = inboxIds.map(id => map.get(id) || `#${id}`).join(' · ');
    const snippet = names.length > 40 ? `${names.slice(0, 40)}…` : names;
    return withAttr(conditionOperatorLabel(attr, op), snippet);
  }
  if (attr === 'status' && val) {
    return withAttr(conditionOperatorLabel(attr, op), workflowStatusLabel(val));
  }
  if (attr === 'priority' && val) {
    return withAttr(
      conditionOperatorLabel(attr, op),
      workflowPriorityLabel(val)
    );
  }
  if (attr === 'inbox' && val) {
    const map = new Map(inboxesSorted.value.map(i => [i.id, i.name]));
    const n = Number(val);
    const name = map.get(n) || `#${val}`;
    return withAttr(conditionOperatorLabel(attr, op), name);
  }
  if (attr === 'assignee' && val) {
    const name = accountAgentName(Number(val));
    return name
      ? withAttr(conditionOperatorLabel(attr, op), name)
      : `${attrShort} · ${conditionOperatorLabel(attr, op)}`;
  }
  if (attr === 'label' && val) {
    const title = accountLabelTitle(Number(val));
    return title
      ? withAttr(conditionOperatorLabel(attr, op), title)
      : `${attrShort} · ${conditionOperatorLabel(attr, op)}`;
  }
  if (attr === 'message_content' && val) {
    const snippet = val.length > 40 ? `${val.slice(0, 40)}…` : val;
    return withAttr(conditionOperatorLabel(attr, op), snippet);
  }
  return `${attrShort} · ${conditionOperatorLabel(attr, op)}`;
};

const nodeHeight = node => {
  if (isConditionActionNode(node)) {
    return conditionNodeHeight(node);
  }
  if (isAiAgentActionNode(node)) {
    return AI_AGENT_NODE_SIZE;
  }
  const lines =
    (triggerMessageCardTypeLabelsText(node) ? 1 : 0) +
    (triggerMessageCardContentRow(node) ? 1 : 0) +
    (triggerInboxCardLabelsText(node) ? 1 : 0) +
    (triggerLabelAddedCardTitle(node) ? 1 : 0) +
    (actionSendMessageCardSnippet(node) ? 1 : 0) +
    (actionPrivateNoteCardSnippet(node) ? 1 : 0) +
    (actionAddLabelCardTitle(node) ? 1 : 0) +
    (actionRemoveLabelCardTitle(node) ? 1 : 0) +
    (actionAssignAgentCardName(node) ? 1 : 0) +
    (actionAssignTeamCardName(node) ? 1 : 0) +
    (actionRemoveAgentHint(node) ? 1 : 0) +
    (actionRemoveTeamCardSummary(node) ? 1 : 0) +
    (summarizeWorkflowConversationCustomAttributes(node) ? 1 : 0) +
    (actionTransferInboxCardName(node) ? 1 : 0) +
    (actionChangeStatusCardLabel(node) ? 1 : 0) +
    (actionChangePriorityCardLabel(node) ? 1 : 0) +
    (summarizeWorkflowWaitActionMinutes(node) ? 1 : 0) +
    (summarizeWorkflowHttpRequest(node) ? 1 : 0) +
    (kanbanTriggerCardSummary(node) ? 1 : 0) +
    (kanbanActionCardSummary(node) ? 1 : 0);
  if (!lines) return NODE_H;
  return NODE_H + lines * NODE_SUMMARY_LINE_H + 6;
};

const outputPos = (node, sourceHandle = null) => {
  const w = nodeWidth(node);
  const h = nodeHeight(node);
  const sh = normalizeSourceHandle(sourceHandle);
  if (isConditionActionNode(node)) {
    return {
      x: node.position.x + w - NODE_FRAME_BORDER_W,
      y: conditionOutputY(node, sh),
    };
  }
  return {
    x: node.position.x + w - NODE_FRAME_BORDER_W,
    y: node.position.y + h / 2,
  };
};

const inputPos = node => ({
  x: node.position.x + NODE_FRAME_BORDER_W,
  y: node.position.y + nodeHeight(node) / 2,
});

const edgePath = edge => {
  const s = nodeById(edge.source);
  const t = nodeById(edge.target);
  if (!s || !t) return '';
  const a = outputPos(s, edge.sourceHandle);
  const b = inputPos(t);
  const mx = (a.x + b.x) / 2;
  return `M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`;
};

/** Ponto médio da curva cúbica usada em `edgePath` (t = 0.5). */
const edgeMidpoint = edge => {
  const s = nodeById(edge.source);
  const t = nodeById(edge.target);
  if (!s || !t) return null;
  const a = outputPos(s, edge.sourceHandle);
  const b = inputPos(t);
  const mx = (a.x + b.x) / 2;
  return {
    x: (a.x + b.x) / 2,
    y: 0.125 * a.y + 0.375 * a.y + 0.375 * b.y + 0.125 * b.y,
  };
};

const MINIMAP_PAD = 96;
const MINIMAP_W = 176;
const MINIMAP_H = 120;

const graphBounds = computed(() => {
  const nodes = graph.nodes;
  if (!nodes.length) {
    return {
      minX: -MINIMAP_PAD,
      minY: -MINIMAP_PAD,
      maxX: 520 + MINIMAP_PAD,
      maxY: 360 + MINIMAP_PAD,
    };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < nodes.length; i += 1) {
    const n = nodes[i];
    const h = nodeHeight(n);
    minX = Math.min(minX, n.position.x);
    minY = Math.min(minY, n.position.y);
    maxX = Math.max(maxX, n.position.x + nodeWidth(n));
    maxY = Math.max(maxY, n.position.y + h);
  }
  return {
    minX: minX - MINIMAP_PAD,
    minY: minY - MINIMAP_PAD,
    maxX: maxX + MINIMAP_PAD,
    maxY: maxY + MINIMAP_PAD,
  };
});

/**
 * ViewBox do minimapa = união (grafo + vista em coords do grafo), com a mesma
 * razão largura/altura do widget. Assim preserveAspectRatio="none" não estica
 * os nós de forma anisotrópica quando o pan muda o retângulo de união.
 */
const minimapFrame = computed(() => {
  const z = Math.max(0.001, zoom.value);
  const cw = Math.max(1, canvasSize.w);
  const ch = Math.max(1, canvasSize.h);
  const vx = -viewport.x / z;
  const vy = -viewport.y / z;
  const vw = cw / z;
  const vh = ch / z;
  const b = graphBounds.value;
  const pad = Math.max(72, vw * 0.14, vh * 0.14);
  let minX = Math.min(b.minX, vx - pad);
  let minY = Math.min(b.minY, vy - pad);
  let maxX = Math.max(b.maxX, vx + vw + pad);
  let maxY = Math.max(b.maxY, vy + vh + pad);
  let w = maxX - minX;
  let h = maxY - minY;
  const minSpan = 160;
  if (w < minSpan) {
    const cx = (minX + maxX) / 2;
    w = minSpan;
    minX = cx - w / 2;
    maxX = cx + w / 2;
  }
  if (h < minSpan) {
    const cy = (minY + maxY) / 2;
    h = minSpan;
    minY = cy - h / 2;
    maxY = cy + h / 2;
  }
  const targetAspect = MINIMAP_W / MINIMAP_H;
  const cx = minX + w / 2;
  const cy = minY + h / 2;
  const curAspect = w / h;
  if (curAspect > targetAspect) {
    h = w / targetAspect;
    minY = cy - h / 2;
  } else if (curAspect < targetAspect) {
    w = h * targetAspect;
    minX = cx - w / 2;
  }
  return {
    minX,
    minY,
    w,
    h,
    viewport: { x: vx, y: vy, w: vw, h: vh },
  };
});

const minimapViewBox = computed(() => {
  const f = minimapFrame.value;
  return `${f.minX} ${f.minY} ${f.w} ${f.h}`;
});

const minimapStroke = computed(() => {
  const f = minimapFrame.value;
  const span = Math.max(120, Math.min(f.w, f.h));
  return Math.max(6, Math.round(span * 0.006));
});

const minimapSvgRef = ref(null);
const minimapDragging = ref(false);
let minimapDragStart = { gx: 0, gy: 0, vx: 0, vy: 0 };

const graphPosFromMinimapClient = (e, svgEl) => {
  if (!svgEl) return { gx: 0, gy: 0 };
  const rect = svgEl.getBoundingClientRect();
  const fx = (e.clientX - rect.left) / Math.max(1, rect.width);
  const fy = (e.clientY - rect.top) / Math.max(1, rect.height);
  const f = minimapFrame.value;
  return { gx: f.minX + fx * f.w, gy: f.minY + fy * f.h };
};

const onMinimapMove = e => {
  if (!minimapDragging.value) return;
  const svg = minimapSvgRef.value;
  if (!svg) return;
  const p = graphPosFromMinimapClient(e, svg);
  const dgx = p.gx - minimapDragStart.gx;
  const dgy = p.gy - minimapDragStart.gy;
  const z = zoom.value;
  viewport.x = minimapDragStart.vx - dgx * z;
  viewport.y = minimapDragStart.vy - dgy * z;
};

const onMinimapUp = () => {
  window.removeEventListener('mousemove', onMinimapMove);
  window.removeEventListener('mouseup', onMinimapUp);
  minimapDragging.value = false;
  snapViewportIfZoomOne();
};

const onMinimapMouseDown = e => {
  if (e.button !== 0) return;
  e.stopPropagation();
  e.preventDefault();
  const svg = minimapSvgRef.value;
  if (!svg) return;
  const p = graphPosFromMinimapClient(e, svg);
  minimapDragging.value = true;
  minimapDragStart = { gx: p.gx, gy: p.gy, vx: viewport.x, vy: viewport.y };
  window.addEventListener('mousemove', onMinimapMove);
  window.addEventListener('mouseup', onMinimapUp);
};

const deleteEdge = edge => {
  const i = graph.edges.findIndex(e => e.id === edge.id);
  if (i !== -1) graph.edges.splice(i, 1);
};

/** Drag from output (right handle) to input (left handle) on another node — n8n-style */
const linkDrag = reactive({
  active: false,
  sourceId: null,
  sourceHandle: null,
  curX: 0,
  curY: 0,
});

const clientToGraph = e => {
  const wrap = canvasRef.value;
  if (!wrap) return { x: 0, y: 0 };
  const rect = wrap.getBoundingClientRect();
  const z = zoom.value;
  return {
    x: (e.clientX - rect.left - viewport.x) / z,
    y: (e.clientY - rect.top - viewport.y) / z,
  };
};

const ghostLinkPath = computed(() => {
  if (!linkDrag.active || !linkDrag.sourceId) return '';
  const s = nodeById(linkDrag.sourceId);
  if (!s) return '';
  const a = outputPos(s, linkDrag.sourceHandle);
  const b = { x: linkDrag.curX, y: linkDrag.curY };
  const mx = (a.x + b.x) / 2;
  return `M ${a.x} ${a.y} C ${mx} ${a.y}, ${mx} ${b.y}, ${b.x} ${b.y}`;
});

const onLinkKeyEscape = e => {
  if (e.key === 'Escape') cancelLinkDrag();
};

const resetLinkDrag = () => {
  linkDrag.active = false;
  linkDrag.sourceId = null;
  linkDrag.sourceHandle = null;
  linkDrag.curX = 0;
  linkDrag.curY = 0;
};

const onOutputPointerDown = (node, e) => {
  if (e.button !== 0) return;
  e.stopPropagation();
  e.preventDefault();
  const pt = clientToGraph(e);
  const raw = e.currentTarget?.dataset?.sourceHandle;
  linkDrag.active = true;
  linkDrag.sourceId = node.id;
  linkDrag.sourceHandle = normalizeSourceHandle(raw || null);
  linkDrag.curX = pt.x;
  linkDrag.curY = pt.y;
  window.addEventListener('mousemove', onLinkMove);
  window.addEventListener('mouseup', onLinkUp, true);
  window.addEventListener('keydown', onLinkKeyEscape);
};

const onLinkMove = e => {
  if (!linkDrag.active) return;
  const pt = clientToGraph(e);
  linkDrag.curX = pt.x;
  linkDrag.curY = pt.y;
};

const onLinkUp = e => {
  if (!linkDrag.active) return;
  const sourceId = linkDrag.sourceId;
  window.removeEventListener('mousemove', onLinkMove);
  window.removeEventListener('mouseup', onLinkUp, true);
  window.removeEventListener('keydown', onLinkKeyEscape);

  const hit = document.elementFromPoint(e.clientX, e.clientY);
  const inputEl = hit?.closest?.('[data-flow-input]');
  const targetId = inputEl?.dataset?.nodeId;
  const sourceHandle = linkDrag.sourceHandle;

  if (
    targetId &&
    targetId !== sourceId &&
    !graph.edges.some(
      edge =>
        edge.source === sourceId &&
        edge.target === targetId &&
        (edge.sourceHandle || null) === (sourceHandle || null)
    )
  ) {
    const next = {
      id: crypto.randomUUID(),
      source: sourceId,
      target: targetId,
    };
    if (sourceHandle) next.sourceHandle = sourceHandle;
    graph.edges.push(next);
  }

  resetLinkDrag();
};

const cancelLinkDrag = () => {
  if (!linkDrag.active) return;
  window.removeEventListener('mousemove', onLinkMove);
  window.removeEventListener('mouseup', onLinkUp, true);
  window.removeEventListener('keydown', onLinkKeyEscape);
  resetLinkDrag();
};

const kindLabel = (type, kind) => {
  const map = type === 'trigger' ? TRIGGER_LABELS : ACTION_LABELS;
  return map[kind] || String(kind);
};

const sidebarNodeSearch = ref('');

const filterKindsByQuery = (kinds, type) => {
  const q = sidebarNodeSearch.value.trim().toLowerCase();
  if (!q) return kinds;
  return kinds.filter(kind => {
    const label = kindLabel(type, kind).toLowerCase();
    const kindKey = String(kind).toLowerCase().replace(/_/g, ' ');
    return label.includes(q) || kindKey.includes(q);
  });
};

const filteredTriggerKinds = computed(() =>
  filterKindsByQuery(TRIGGER_KINDS, 'trigger')
);

const filteredActionKinds = computed(() =>
  filterKindsByQuery(ACTION_KINDS, 'action')
);

const deleteNode = node => {
  const id = node.id;
  const idx = graph.nodes.findIndex(n => n.id === id);
  if (idx === -1) return;
  graph.nodes.splice(idx, 1);
  for (let i = graph.edges.length - 1; i >= 0; i -= 1) {
    const e = graph.edges[i];
    if (e.source === id || e.target === id) graph.edges.splice(i, 1);
  }
  if (selectedNodeId.value === id) closeNodePanel();
};

const duplicateNode = node => {
  const id = crypto.randomUUID();
  graph.nodes.push({
    id,
    type: node.type,
    position: {
      x: node.position.x + 24,
      y: node.position.y + 24,
    },
    data: JSON.parse(JSON.stringify(node.data)),
  });
  selectedNodeId.value = id;
};

const addNode = (type, kind, dropAt = null) => {
  const el = canvasRef.value;
  const rect = el?.getBoundingClientRect();
  const z = zoom.value;
  let placeW = NODE_W;
  if (type === 'action' && kind === 'condition') {
    placeW = CONDITION_NODE_W;
  } else if (type === 'action' && kind === 'ai_agent') {
    placeW = AI_AGENT_NODE_SIZE;
  }
  const placeH =
    type === 'action' && kind === 'ai_agent' ? AI_AGENT_NODE_SIZE : NODE_H;

  let x;
  let y;

  const fromDrop =
    dropAt &&
    typeof dropAt.clientX === 'number' &&
    typeof dropAt.clientY === 'number' &&
    rect;

  if (fromDrop) {
    const gx = (dropAt.clientX - rect.left - viewport.x) / z;
    const gy = (dropAt.clientY - rect.top - viewport.y) / z;
    x = Math.round(gx - placeW / 2);
    y = Math.round(gy - placeH / 2);
  } else {
    const cx = rect ? rect.width / 2 : 320;
    const cy = rect ? rect.height / 2 : 240;
    x = Math.round(
      (cx - viewport.x) / z - NODE_W / 2 + graph.nodes.length * 12
    );
    y = Math.round(
      (cy - viewport.y) / z - NODE_H / 2 + graph.nodes.length * 12
    );
  }
  const id = crypto.randomUUID();
  const data = {
    kind,
    label: kindLabel(type, kind),
  };
  if (type === 'trigger' && kind === 'message_created') {
    data.message_filters = normalizeMessageFilters({});
  }
  if (type === 'trigger' && kind === 'conversation_created') {
    data.conversation_filters = normalizeConversationFilters({});
  }
  if (type === 'trigger' && kind === 'label_added') {
    data.label_id = null;
  }
  if (
    type === 'trigger' &&
    [
      'kanban_item_created',
      'kanban_item_updated',
      'kanban_item_stage_changed',
      'kanban_item_deleted',
    ].includes(kind)
  ) {
    Object.assign(data, normalizeKanbanWorkflowTriggerFields(kind, {}));
  }
  if (type === 'action' && kind === 'send_message') {
    data.message_content = '';
  }
  if (type === 'action' && kind === 'add_private_note') {
    data.private_note_content = '';
  }
  if (type === 'action' && kind === 'add_label') {
    data.label_id = null;
  }
  if (type === 'action' && kind === 'remove_label') {
    data.label_id = null;
  }
  if (type === 'action' && kind === 'assign_agent') {
    data.agent_id = null;
  }
  if (type === 'action' && (kind === 'assign_team' || kind === 'remove_team')) {
    data.team_id = null;
  }
  if (type === 'action' && kind === 'set_custom_attributes') {
    Object.assign(
      data,
      normalizeWorkflowConversationCustomAttributesFields({})
    );
  }
  if (type === 'action' && kind === 'transfer_inbox') {
    data.inbox_id = null;
  }
  if (type === 'action' && kind === 'change_status') {
    data.status = null;
  }
  if (type === 'action' && kind === 'change_priority') {
    data.priority = null;
  }
  if (type === 'action' && kind === 'wait') {
    data.wait_minutes = normalizeWorkflowWaitMinutes(5);
  }
  if (type === 'action' && kind === 'http_request') {
    Object.assign(data, normalizeWorkflowHttpRequestFields({}));
  }
  if (type === 'action' && kind === 'ai_agent') {
    Object.assign(data, normalizeWorkflowAiAgentFields({}));
  }
  if (type === 'action' && kind === 'condition') {
    Object.assign(data, normalizeWorkflowConditionData({}));
  }
  if (type === 'action' && kind === 'kanban_create_item') {
    Object.assign(data, normalizeKanbanCreateItemFields({}));
  }
  if (type === 'action' && kind === 'kanban_move_to_stage') {
    Object.assign(data, normalizeKanbanMoveToStageFields({}));
  }
  if (type === 'action' && kind === 'kanban_assign_agent') {
    Object.assign(data, normalizeKanbanAssignAgentFields({}));
  }
  if (type === 'action' && kind === 'kanban_remove_agent') {
    Object.assign(data, normalizeKanbanRemoveAgentFields({}));
  }
  if (type === 'action' && kind === 'kanban_add_note') {
    Object.assign(data, normalizeKanbanAddNoteFields({}));
  }
  if (type === 'action' && kind === 'kanban_change_item_status') {
    Object.assign(data, normalizeKanbanChangeItemStatusFields({}));
  }
  graph.nodes.push({
    id,
    type,
    position: { x, y },
    data,
  });
};

const draggingPaletteNode = ref(null);

const workflowPaletteDragMimeActive = dt => {
  if (!dt?.types) return false;
  return [...dt.types].some(
    mimeType =>
      String(mimeType).toLowerCase() === FLOW_PALETTE_DRAG_MIME.toLowerCase()
  );
};

const paletteDragAllowsDrop = dt =>
  !!(draggingPaletteNode.value?.type && draggingPaletteNode.value?.kind) ||
  workflowPaletteDragMimeActive(dt);

const clearDraggingPaletteNode = () => {
  draggingPaletteNode.value = null;
};

const workflowPalettePayloadFromTransfer = dt => {
  if (!dt) return null;
  try {
    const raw = dt.getData(FLOW_PALETTE_DRAG_MIME);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        (parsed?.type === 'trigger' || parsed?.type === 'action') &&
        typeof parsed?.kind === 'string'
      ) {
        return { type: parsed.type, kind: parsed.kind };
      }
    }
  } catch {
    //
  }
  try {
    const plain = dt.getData('text/plain');
    if (
      plain &&
      plain.startsWith(FLOW_PALETTE_TEXT_PREFIX) &&
      plain.length > FLOW_PALETTE_TEXT_PREFIX.length
    ) {
      const rest = plain.slice(FLOW_PALETTE_TEXT_PREFIX.length);
      const i = rest.indexOf(':');
      if (i > 0) {
        const dragType = rest.slice(0, i);
        const dragKind = rest.slice(i + 1);
        if ((dragType === 'trigger' || dragType === 'action') && dragKind) {
          return { type: dragType, kind: dragKind };
        }
      }
    }
  } catch {
    //
  }
  return null;
};

const onWorkflowPaletteDragOverCapture = e => {
  if (!paletteDragAllowsDrop(e.dataTransfer)) return;
  e.preventDefault();
  try {
    e.dataTransfer.dropEffect = 'copy';
  } catch {
    //
  }
};

const onWorkflowPaletteDropCapture = e => {
  if (!paletteDragAllowsDrop(e.dataTransfer)) return;
  const payload =
    draggingPaletteNode.value ||
    workflowPalettePayloadFromTransfer(e.dataTransfer);
  clearDraggingPaletteNode();
  if (!payload) return;
  const ok =
    (payload.type === 'trigger' && TRIGGER_KINDS.includes(payload.kind)) ||
    (payload.type === 'action' && ACTION_KINDS.includes(payload.kind));
  if (!ok) return;
  e.preventDefault();
  e.stopPropagation();
  addNode(payload.type, payload.kind, {
    clientX: e.clientX,
    clientY: e.clientY,
  });
};

const onPaletteNodeDragStart = (event, paletteType, paletteKind) => {
  draggingPaletteNode.value = {
    type: paletteType,
    kind: paletteKind,
  };
  const dt = event.dataTransfer;
  if (!dt) return;
  try {
    dt.setData(
      FLOW_PALETTE_DRAG_MIME,
      JSON.stringify({ type: paletteType, kind: paletteKind })
    );
    dt.setData(
      'text/plain',
      `${FLOW_PALETTE_TEXT_PREFIX}${paletteType}:${paletteKind}`
    );
  } catch {
    dt.setData(
      'text/plain',
      `${FLOW_PALETTE_TEXT_PREFIX}${paletteType}:${paletteKind}`
    );
  }
  dt.effectAllowed = 'copy';
};

const stickyMd = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true,
});

const renderStickyMarkdown = raw => {
  const text = typeof raw === 'string' ? raw : '';
  if (!text.trim()) return '';
  return DOMPurify.sanitize(stickyMd.render(text));
};

const stickyEditingId = ref(null);
const stickyTextareaRef = ref(null);

const startStickyEdit = note => {
  stickyEditingId.value = note.id;
  nextTick(() => {
    stickyTextareaRef.value?.focus?.();
  });
};

const onStickyPreviewClick = (note, e) => {
  if (e.target.closest?.('a')) return;
  startStickyEdit(note);
};

const onStickyPreviewKeydown = (note, e) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  if (e.target.closest?.('a')) return;
  startStickyEdit(note);
};

const onStickyTextareaBlur = e => {
  const id = e.target?.dataset?.stickyNoteId;
  if (id && stickyEditingId.value === id) {
    stickyEditingId.value = null;
  }
};

/** Cores da nota (hex) — fora de utilitários Tailwind por pedido do produto. */
const STICKY_HEX = {
  cardBorder: '#EAB308',
  cardBg: '#FEFCE8',
  cardText: '#422006',
  cardShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
  iconStroke: '#B45309',
  deleteColor: '#92400E99',
  deleteHoverBg: '#FDE047',
  deleteHoverColor: '#451A03',
  textareaColor: '#422006',
};

const stickyNoteCardStyle = note => {
  const { width, height } = normalizeStickyDimensions(note.width, note.height);
  return {
    left: `${note.position.x}px`,
    top: `${note.position.y}px`,
    width: `${width}px`,
    height: `${height}px`,
    border: `1px solid ${STICKY_HEX.cardBorder}`,
    backgroundColor: STICKY_HEX.cardBg,
    color: STICKY_HEX.cardText,
    boxShadow: STICKY_HEX.cardShadow,
  };
};

const stickyNoteDeleteStyle = {
  color: STICKY_HEX.deleteColor,
};

const stickyNoteTextareaStyle = {
  color: STICKY_HEX.textareaColor,
};

const onStickyDeleteEnter = e => {
  const el = e.currentTarget;
  el.style.backgroundColor = STICKY_HEX.deleteHoverBg;
  el.style.color = STICKY_HEX.deleteHoverColor;
};

const onStickyDeleteLeave = e => {
  const el = e.currentTarget;
  el.style.backgroundColor = '';
  el.style.color = STICKY_HEX.deleteColor;
};

const addStickyNote = () => {
  const el = canvasRef.value;
  const rect = el?.getBoundingClientRect();
  const cx = rect ? rect.width / 2 : 320;
  const cy = rect ? rect.height / 2 : 240;
  const z = zoom.value;
  const n = graph.sticky_notes.length;
  const x = Math.round(
    (cx - viewport.x) / z - STICKY_DIM.defaultW / 2 + n * 16
  );
  const y = Math.round(
    (cy - viewport.y) / z - STICKY_DIM.defaultH / 2 + n * 16
  );
  const { width, height } = normalizeStickyDimensions(
    STICKY_DIM.defaultW,
    STICKY_DIM.defaultH
  );
  const nid = crypto.randomUUID();
  graph.sticky_notes.push({
    id: nid,
    position: { x, y },
    width,
    height,
    text: '',
  });
  stickyEditingId.value = nid;
  nextTick(() => stickyTextareaRef.value?.focus?.());
};

const deleteStickyNote = note => {
  if (stickyEditingId.value === note.id) stickyEditingId.value = null;
  const i = graph.sticky_notes.findIndex(s => s.id === note.id);
  if (i !== -1) graph.sticky_notes.splice(i, 1);
};

const dragStickyId = ref(null);
let dragStickyStart = { x: 0, y: 0, px: 0, py: 0 };

const onStickyCardMouseDown = (note, e) => {
  if (e.button !== 0) return;
  if (e.target.closest?.('textarea')) return;
  if (e.target.closest?.('[data-flow-sticky-preview]')) return;
  if (e.target.closest?.('[data-flow-sticky-resize]')) return;
  if (e.target.closest?.('[data-flow-sticky-delete]')) return;
  onStickyDragStart(note, e);
};

const onStickyDragStart = (note, e) => {
  if (e.button !== 0) return;
  e.stopPropagation();
  dragStickyId.value = note.id;
  dragStickyStart = {
    x: e.clientX,
    y: e.clientY,
    px: note.position.x,
    py: note.position.y,
  };
  window.addEventListener('mousemove', onStickyDragMove);
  window.addEventListener('mouseup', onStickyDragEnd);
};

const onStickyDragMove = e => {
  if (!dragStickyId.value) return;
  const note = graph.sticky_notes.find(s => s.id === dragStickyId.value);
  if (!note) return;
  const z = zoom.value;
  note.position.x = Math.round(
    dragStickyStart.px + (e.clientX - dragStickyStart.x) / z
  );
  note.position.y = Math.round(
    dragStickyStart.py + (e.clientY - dragStickyStart.y) / z
  );
};

const onStickyDragEnd = () => {
  dragStickyId.value = null;
  window.removeEventListener('mousemove', onStickyDragMove);
  window.removeEventListener('mouseup', onStickyDragEnd);
};

const stickyResize = reactive({
  active: false,
  noteId: null,
  startClientX: 0,
  startClientY: 0,
  startW: 0,
  startH: 0,
});

const onStickyResizeStart = (note, e) => {
  if (e.button !== 0) return;
  e.stopPropagation();
  e.preventDefault();
  const dims = normalizeStickyDimensions(note.width, note.height);
  stickyResize.active = true;
  stickyResize.noteId = note.id;
  stickyResize.startClientX = e.clientX;
  stickyResize.startClientY = e.clientY;
  stickyResize.startW = dims.width;
  stickyResize.startH = dims.height;
  window.addEventListener('mousemove', onStickyResizeMove);
  window.addEventListener('mouseup', onStickyResizeEnd);
};

const onStickyResizeMove = e => {
  if (!stickyResize.active || !stickyResize.noteId) return;
  const note = graph.sticky_notes.find(s => s.id === stickyResize.noteId);
  if (!note) return;
  const z = zoom.value;
  const dw = (e.clientX - stickyResize.startClientX) / z;
  const dh = (e.clientY - stickyResize.startClientY) / z;
  const next = normalizeStickyDimensions(
    stickyResize.startW + dw,
    stickyResize.startH + dh
  );
  note.width = next.width;
  note.height = next.height;
};

const onStickyResizeEnd = () => {
  stickyResize.active = false;
  stickyResize.noteId = null;
  window.removeEventListener('mousemove', onStickyResizeMove);
  window.removeEventListener('mouseup', onStickyResizeEnd);
};

const onNodeClick = node => {
  if (dragMovedDuring.value) return;
  selectedNodeId.value = node.id;
};

const onCanvasMouseDown = e => {
  if (e.target.closest('[data-flow-output], [data-flow-input]')) return;
  if (e.target.closest('[data-flow-edge-delete]')) return;
  if (e.target.closest('[data-flow-node]')) return;
  if (e.target.closest('[data-flow-sticky]')) return;
  if (e.button !== 0 && e.button !== 1) return;
  closeNodePanel();
  e.preventDefault();
  isPanning.value = true;
  panStart = { x: e.clientX, y: e.clientY, vx: viewport.x, vy: viewport.y };
  window.addEventListener('mousemove', onPanMove);
  window.addEventListener('mouseup', onPanUp);
};

const onPanMove = e => {
  if (!isPanning.value) return;
  const nx = panStart.vx + (e.clientX - panStart.x);
  const ny = panStart.vy + (e.clientY - panStart.y);
  if (zoomIsOne.value) {
    viewport.x = Math.round(nx);
    viewport.y = Math.round(ny);
  } else {
    viewport.x = nx;
    viewport.y = ny;
  }
};

const onPanUp = () => {
  isPanning.value = false;
  window.removeEventListener('mousemove', onPanMove);
  window.removeEventListener('mouseup', onPanUp);
  snapViewportIfZoomOne();
};

const onNodeDragStart = (node, e) => {
  if (e.button !== 0) return;
  e.stopPropagation();
  dragMovedDuring.value = false;
  dragNode.value = node.id;
  dragStart = {
    x: e.clientX,
    y: e.clientY,
    px: node.position.x,
    py: node.position.y,
  };
  window.addEventListener('mousemove', onNodeDragMove);
  window.addEventListener('mouseup', onNodeDragEnd);
};

const onNodeDragMove = e => {
  if (!dragNode.value) return;
  if (
    Math.abs(e.clientX - dragStart.x) > 5 ||
    Math.abs(e.clientY - dragStart.y) > 5
  ) {
    dragMovedDuring.value = true;
  }
  const node = nodeById(dragNode.value);
  if (!node) return;
  const z = zoom.value;
  node.position.x = Math.round(dragStart.px + (e.clientX - dragStart.x) / z);
  node.position.y = Math.round(dragStart.py + (e.clientY - dragStart.y) / z);
};

const onNodeDragEnd = () => {
  dragNode.value = null;
  window.removeEventListener('mousemove', onNodeDragMove);
  window.removeEventListener('mouseup', onNodeDragEnd);
  setTimeout(() => {
    dragMovedDuring.value = false;
  }, 0);
};

const saveNow = async () => {
  await persist();
};

watch(selectedNodeId, id => {
  if (id) window.addEventListener('keydown', onEscapePanel);
  else {
    window.removeEventListener('keydown', onEscapePanel);
    workflowVariablesPanelExpanded.value = true;
  }
});

watch(stickyEditingId, (id, _prev, onCleanup) => {
  if (!id) return;
  const onPointerDownCapture = e => {
    const active = stickyEditingId.value;
    if (!active) return;
    const wrap = e.target.closest?.('[data-flow-sticky]');
    const wid = wrap?.dataset?.stickyNoteId;
    if (wid !== undefined && String(wid) === String(active)) return;
    stickyEditingId.value = null;
  };
  window.addEventListener('pointerdown', onPointerDownCapture, true);
  onCleanup(() =>
    window.removeEventListener('pointerdown', onPointerDownCapture, true)
  );
});

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('funnel/fetch').catch(() => {});
  const ok = await load();
  refreshPinnedExecution();
  if (!ok) return;
});

watch(workflowId, async (next, prev) => {
  if (!next || String(next) === String(prev)) return;
  await load();
  refreshPinnedExecution();
});

onUnmounted(() => {
  window.removeEventListener('keydown', onEscapePanel);
  cancelLinkDrag();
  onPanUp();
  onNodeDragEnd();
  onStickyDragEnd();
  onStickyResizeEnd();
  onMinimapUp();
});
</script>

<template>
  <div
    class="fixed inset-0 z-[100] flex flex-col bg-n-surface-1 text-n-slate-12 border-t border-n-weak"
  >
    <input
      ref="importFileInputRef"
      type="file"
      accept="application/json,.json"
      class="hidden"
      @change="onImportFileChange"
    />
    <header
      class="relative z-[70] flex flex-shrink-0 items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2 sm:px-4"
    >
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        title="Voltar"
        @click="goBack"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-5 shrink-0"
          aria-hidden="true"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>
      <input
        v-model="workflowName"
        type="text"
        class="reset-base min-w-0 flex-1 w-full m-0 !mb-0 h-auto min-h-0 rounded-none border-0 !border-0 !bg-transparent py-1 px-1 text-base font-medium leading-snug text-n-slate-12 shadow-none ring-0 ring-offset-0 outline-none !outline-0 placeholder:text-n-slate-10 hover:border-0 hover:shadow-none focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 dark:!bg-transparent"
        placeholder="Fluxo sem título"
      />
      <div class="flex shrink-0 items-center gap-2" title="Ativo">
        <span
          class="hidden text-sm text-n-slate-11 whitespace-nowrap sm:inline"
        >
          Ativo
        </span>
        <Switch
          v-model="workflowActive"
          :disabled="
            isLoading ||
            isSaving ||
            isDeleting ||
            isImporting ||
            isUpdatingActive
          "
          @change="persistWorkflowActive"
        />
      </div>
      <Button
        v-if="workflowId"
        ghost
        slate
        label="Historico"
        size="sm"
        class="shrink-0"
        :disabled="isLoading"
        @click="openExecutionsHistory"
      />
      <div class="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
        <span
          v-if="isImporting"
          class="text-xs text-n-slate-11 whitespace-nowrap"
        >
          {{ t('CONVERSATION_WORKFLOW.FLOW_EDITOR.IMPORTING') }}
        </span>
        <span
          v-else-if="isSaving"
          class="text-xs text-n-slate-11 whitespace-nowrap"
        >
          Salvando…
        </span>
        <span
          v-else-if="isDeleting"
          class="text-xs text-n-slate-11 whitespace-nowrap"
        >
          Excluindo…
        </span>
        <Button
          v-if="pinnedExecutionPreviewActive"
          label="Limpar dados"
          icon="i-lucide-eraser"
          color="slate"
          variant="outline"
          size="sm"
          class="shrink-0"
          :title="CLEAR_PINNED_EXECUTION_TITLE"
          :disabled="isDeleting || isImporting"
          @click="onClearPinnedExecution"
        />
        <Button
          label="Salvar"
          size="sm"
          :disabled="isDeleting || isImporting"
          @click="saveNow"
        />
        <div
          v-on-clickaway="() => (showFlowMoreMenu = false)"
          class="relative shrink-0"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            variant="ghost"
            size="sm"
            class="shrink-0"
            :class="showFlowMoreMenu ? 'bg-n-alpha-2' : ''"
            :disabled="isLoading || isSaving || isDeleting || isImporting"
            :aria-label="t('CONVERSATION_WORKFLOW.FLOW_EDITOR.MORE_OPTIONS')"
            @click="showFlowMoreMenu = !showFlowMoreMenu"
          />
          <DropdownMenu
            v-if="showFlowMoreMenu"
            :menu-items="flowMoreMenuItems"
            class="ltr:right-0 rtl:left-0 mt-1 top-full w-52"
            @action="handleFlowMoreMenuAction($event)"
          />
        </div>
      </div>
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      Carregando…
    </div>

    <div v-else class="flex flex-1 min-h-0">
      <aside
        class="flex w-72 max-w-[min(18rem,100vw)] flex-shrink-0 flex-col overflow-hidden border-r border-n-weak bg-n-solid-1"
      >
        <div
          class="sticky top-0 z-10 shrink-0 border-b border-n-weak/90 bg-gradient-to-b from-n-solid-1 via-n-solid-1 to-n-solid-1/95 px-3 pb-3 pt-3 backdrop-blur-sm supports-[backdrop-filter]:bg-n-solid-1/85"
        >
          <label class="sr-only" for="flow-sidebar-node-search">
            Buscar gatilhos e ações
          </label>
          <div class="relative">
            <span
              class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 shrink-0 text-n-slate-10 i-lucide-search"
              aria-hidden="true"
            />
            <input
              id="flow-sidebar-node-search"
              v-model="sidebarNodeSearch"
              type="search"
              autocomplete="off"
              class="reset-base box-border w-full rounded-xl border border-n-weak bg-n-solid-2 py-2.5 pl-10 pr-3 text-sm leading-tight text-n-slate-12 shadow-inner outline-none transition-[border-color,box-shadow] placeholder:text-n-slate-11 focus-visible:border-n-teal-9/50 focus-visible:ring-2 focus-visible:ring-n-teal-9"
              placeholder="Buscar gatilhos e ações…"
            />
          </div>
        </div>

        <div
          class="flex-1 space-y-4 overflow-y-auto overscroll-contain px-2.5 pb-4 pt-2 [scrollbar-width:thin] [-ms-overflow-style:auto] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-n-slate-8/40 [&::-webkit-scrollbar-track]:bg-transparent"
        >
          <section aria-labelledby="flow-editor-palette-triggers">
            <div
              id="flow-editor-palette-triggers"
              class="mb-2 flex items-center gap-2 px-0.5"
            >
              <span
                class="flex size-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-amber-500/90 to-orange-600/95 text-white shadow-sm"
                aria-hidden="true"
              >
                <span class="size-3.5 shrink-0 text-white i-lucide-zap" />
              </span>
              <span
                class="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-slate-10"
              >
                Gatilhos
              </span>
            </div>
            <template v-if="filteredTriggerKinds.length">
              <div class="flex flex-col gap-1.5">
                <button
                  v-for="kind in filteredTriggerKinds"
                  :key="kind"
                  type="button"
                  draggable="true"
                  class="group relative flex w-full items-center gap-3 overflow-hidden rounded-xl border border-n-weak border-l-[3px] bg-n-solid-1 px-3 py-2 text-left outline-none shadow-sm ring-0 transition-[border-color,box-shadow,transform] hover:z-[1] hover:border-n-strong hover:shadow-md focus-visible:ring-2 focus-visible:ring-n-teal-9 active:scale-[0.99]"
                  :style="{
                    ...triggerSidebarRowStyle(kind),
                    ...triggerSidebarBorderLeftStyle(kind),
                  }"
                  @dragstart.stop="
                    onPaletteNodeDragStart($event, 'trigger', kind)
                  "
                  @dragend="clearDraggingPaletteNode"
                  @mouseenter="sidebarTriggerHoverKind = kind"
                  @mouseleave="
                    sidebarTriggerHoverKind = null;
                    sidebarTriggerActiveKind = null;
                  "
                  @mousedown="sidebarTriggerActiveKind = kind"
                  @mouseup="sidebarTriggerActiveKind = null"
                  @click="addNode('trigger', kind)"
                >
                  <span
                    class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-solid-2 ring-1 ring-n-strong/20 transition-[background-color,box-shadow] group-hover:bg-n-solid-3 group-hover:shadow-sm group-hover:ring-n-strong/35"
                  >
                    <span
                      class="i-lucide-zap size-4 shrink-0"
                      :style="triggerSidebarIconStyle(kind)"
                    />
                  </span>
                  <span
                    class="truncate text-sm font-medium leading-snug text-n-slate-12"
                    >{{ kindLabel('trigger', kind) }}</span>
                  <span
                    class="ml-auto size-4 shrink-0 text-n-slate-10 opacity-0 transition-opacity i-lucide-plus group-hover:opacity-100"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </template>
            <p
              v-else
              class="rounded-xl border border-dashed border-n-weak px-3 py-3 text-center text-xs leading-snug text-n-slate-11"
            >
              Nenhum gatilho corresponde à busca.
            </p>
          </section>

          <section aria-labelledby="flow-editor-palette-actions">
            <div
              id="flow-editor-palette-actions"
              class="mb-2 flex items-center gap-2 px-0.5"
            >
              <span
                class="flex size-6 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-teal-500/90 to-cyan-600/95 text-white shadow-sm"
                aria-hidden="true"
              >
                <span class="size-3.5 shrink-0 text-white i-lucide-blocks" />
              </span>
              <span
                class="text-[11px] font-semibold uppercase tracking-[0.14em] text-n-slate-10"
              >
                Ações
              </span>
            </div>
            <template v-if="filteredActionKinds.length">
              <div class="flex flex-col gap-1.5">
                <button
                  v-for="kind in filteredActionKinds"
                  :key="kind"
                  type="button"
                  draggable="true"
                  class="group flex w-full cursor-grab items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-left outline-none shadow-sm ring-0 transition-[border-color,box-shadow,transform,background-color] hover:z-[1] hover:border-n-strong hover:bg-n-solid-3 hover:shadow-md focus-visible:ring-2 focus-visible:ring-n-teal-9 active:cursor-grabbing active:scale-[0.99]"
                  @dragstart.stop="
                    onPaletteNodeDragStart($event, 'action', kind)
                  "
                  @dragend="clearDraggingPaletteNode"
                  @click="addNode('action', kind)"
                >
                  <span
                    class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-solid-3 ring-1 ring-n-strong/15 transition-colors group-hover:bg-n-alpha-3 group-hover:ring-n-strong/25"
                  >
                    <span
                      class="size-4 shrink-0 text-n-teal-11 transition-transform group-hover:scale-105"
                      :class="flowActionKindLucideIcon(kind)"
                    />
                  </span>
                  <span
                    class="truncate text-sm font-medium leading-snug text-n-slate-12"
                    >{{ kindLabel('action', kind) }}</span>
                  <span
                    class="ml-auto size-4 shrink-0 text-n-slate-10 opacity-0 transition-opacity i-lucide-plus group-hover:opacity-100"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </template>
            <p
              v-else
              class="rounded-xl border border-dashed border-n-weak px-3 py-3 text-center text-xs leading-snug text-n-slate-11"
            >
              Nenhuma ação corresponde à busca.
            </p>
          </section>
        </div>
      </aside>

      <div class="flex min-h-0 min-w-0 flex-1 overflow-x-auto">
        <div
          ref="canvasRef"
          class="relative min-h-0 min-w-0 flex-1 overflow-hidden bg-n-alpha-2 select-none bg-[length:24px_24px] [background-image:radial-gradient(circle_at_center,rgb(148_163_184/0.25)_1px,transparent_1px)] dark:[background-image:radial-gradient(circle_at_center,rgb(71_85_105/0.45)_1px,transparent_1px)]"
          :class="[
            linkDrag.active
              ? 'cursor-crosshair'
              : 'cursor-grab active:cursor-grabbing',
          ]"
          @mousedown="onCanvasMouseDown"
          @dragover.capture="onWorkflowPaletteDragOverCapture"
          @drop.capture="onWorkflowPaletteDropCapture"
        >
          <div
            class="pointer-events-auto absolute top-3 right-3 z-[62] flex items-center gap-2"
          >
            <button
              type="button"
              data-flow-add-sticky
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-11 shadow-md transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              :title="t('CONVERSATION_WORKFLOW.FLOW_EDITOR.ADD_STICKY_NOTE')"
              @mousedown.stop
              @click="addStickyNote"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="size-4 shrink-0"
                aria-hidden="true"
              >
                <path
                  d="M15.5 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V7.5L15.5 3Z"
                />
                <path d="M15 3v6h6" />
              </svg>
            </button>
            <Transition
              enter-active-class="transition duration-[340ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
              enter-from-class="opacity-0 scale-95 -translate-x-1"
              enter-to-class="opacity-100 scale-100 translate-x-0"
              leave-active-class="transition duration-[220ms] ease-in"
              leave-from-class="opacity-100 scale-100 translate-x-0"
              leave-to-class="opacity-0 scale-95 -translate-x-1"
            >
              <button
                v-if="selectedNode && !workflowVariablesPanelExpanded"
                type="button"
                class="inline-flex h-8 w-8 shrink-0 origin-right items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-11 shadow-md transition-colors duration-200 ease-out hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 motion-reduce:transition-none"
                title="Mostrar painel «Dados entre nós»"
                @mousedown.stop
                @click="workflowVariablesPanelExpanded = true"
              >
                <span
                  class="i-lucide-chevrons-left size-4 shrink-0"
                  aria-hidden="true"
                />
              </button>
            </Transition>
          </div>
          <div
            class="pointer-events-auto absolute bottom-3 left-3 z-[60] flex items-center gap-0.5 rounded-lg border border-n-weak bg-n-solid-1 p-0.5 shadow-md"
            @mousedown.stop
          >
            <button
              type="button"
              title="Diminuir zoom"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 disabled:pointer-events-none disabled:opacity-40"
              :disabled="zoomAtMin"
              @click="zoomOut"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                class="size-4 shrink-0"
                aria-hidden="true"
              >
                <path d="M5 12h14" />
              </svg>
            </button>
            <button
              type="button"
              title="Redefinir zoom (100%) — Ctrl ou ⌘ + rolagem no quadro"
              class="min-w-[3rem] rounded-md px-1.5 py-1 text-center text-xs font-medium tabular-nums text-n-slate-12 transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
              @click="resetZoom"
            >
              {{ zoomPercentLabel }}
            </button>
            <button
              type="button"
              title="Aumentar zoom"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 disabled:pointer-events-none disabled:opacity-40"
              :disabled="zoomAtMax"
              @click="zoomIn"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                class="size-4 shrink-0"
                aria-hidden="true"
              >
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
            </button>
          </div>
          <div
            class="pointer-events-auto absolute bottom-3 right-3 z-[60] overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-md"
            title="Minimapa — arraste para mover a vista"
            @mousedown.stop
          >
            <svg
              ref="minimapSvgRef"
              class="block cursor-grab text-n-slate-8 active:cursor-grabbing"
              :width="MINIMAP_W"
              :height="MINIMAP_H"
              :viewBox="minimapViewBox"
              preserveAspectRatio="none"
              @mousedown="onMinimapMouseDown"
            >
              <rect
                :x="minimapFrame.minX"
                :y="minimapFrame.minY"
                :width="minimapFrame.w"
                :height="minimapFrame.h"
                class="fill-n-alpha-2"
              />
              <template v-for="edge in graph.edges" :key="`mm-e-${edge.id}`">
                <path
                  v-if="edgePath(edge)"
                  :d="edgePath(edge)"
                  fill="none"
                  stroke="currentColor"
                  class="text-n-slate-9 opacity-60"
                  :stroke-width="Math.max(8, minimapStroke * 0.55)"
                  stroke-linecap="round"
                />
              </template>
              <rect
                v-for="node in graph.nodes"
                :key="`mm-n-${node.id}`"
                :x="node.position.x"
                :y="node.position.y"
                :width="nodeWidth(node)"
                :height="nodeHeight(node)"
                :rx="isAiAgentActionNode(node) ? nodeWidth(node) / 2 : 10"
                :ry="isAiAgentActionNode(node) ? nodeWidth(node) / 2 : 10"
                :class="
                  node.type === 'trigger'
                    ? 'fill-n-amber-9/25 stroke-n-amber-10/50'
                    : isAiAgentActionNode(node)
                      ? 'fill-violet-500/35 stroke-violet-400/70'
                      : 'fill-n-teal-9/20 stroke-n-teal-10/45'
                "
                :stroke-width="minimapStroke"
              />
              <rect
                :x="minimapFrame.viewport.x"
                :y="minimapFrame.viewport.y"
                :width="minimapFrame.viewport.w"
                :height="minimapFrame.viewport.h"
                class="pointer-events-none fill-n-teal-9/15 stroke-n-teal-10"
                :stroke-width="minimapStroke"
              />
            </svg>
          </div>
          <div
            class="absolute inset-0 origin-top-left isolate"
            :class="zoomIsOne ? '' : 'will-change-transform'"
            :style="canvasLayerTransformStyle"
          >
            <div
              class="pointer-events-none absolute inset-0 z-0 overflow-visible"
            >
              <div
                v-for="note in graph.sticky_notes"
                :key="note.id"
                data-flow-sticky
                :data-sticky-note-id="note.id"
                class="group/sticky pointer-events-auto absolute z-0 flex min-h-0 min-w-0 cursor-grab flex-col overflow-hidden rounded-md p-2 active:cursor-grabbing"
                :style="stickyNoteCardStyle(note)"
                @mousedown="onStickyCardMouseDown(note, $event)"
              >
                <div
                  v-if="stickyEditingId !== note.id"
                  data-flow-sticky-preview
                  class="flex-1 min-h-0 min-w-0 cursor-text overflow-y-auto pb-5 pr-7 pt-1 text-sm leading-snug"
                  :style="{ color: STICKY_HEX.textareaColor }"
                  role="region"
                  tabindex="0"
                  :aria-label="
                    t('CONVERSATION_WORKFLOW.FLOW_EDITOR.STICKY_PREVIEW_HINT')
                  "
                  @click="onStickyPreviewClick(note, $event)"
                  @keydown="onStickyPreviewKeydown(note, $event)"
                >
                  <div
                    v-if="String(note.text || '').trim()"
                    class="sticky-note-md prose prose-sm max-w-none text-[#422006] prose-headings:mb-1 prose-headings:mt-2 prose-headings:font-semibold first:prose-headings:mt-0 prose-headings:text-[#422006] prose-p:my-1 prose-p:text-[#422006] prose-strong:text-[#422006] prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-a:text-[#B45309] [&_a]:break-all"
                    v-html="renderStickyMarkdown(note.text)"
                  />
                  <p v-else class="text-[#92400EB8]">
                    {{
                      t('CONVERSATION_WORKFLOW.FLOW_EDITOR.STICKY_PLACEHOLDER')
                    }}
                  </p>
                </div>
                <textarea
                  v-else
                  ref="stickyTextareaRef"
                  v-model="note.text"
                  :data-sticky-note-id="note.id"
                  class="reset-base m-0 mb-0 min-h-0 w-full min-w-0 flex-1 cursor-text resize-none border-0 border-transparent bg-transparent p-0 pb-5 pr-7 pt-1 text-sm leading-snug shadow-none ring-0 ring-offset-0 outline-none outline-0 !outline-none placeholder:text-[#92400EB8] hover:border-transparent hover:shadow-none hover:!outline-none focus:border-transparent focus:shadow-none focus:!outline-none focus:outline-none focus:ring-0 focus-visible:!outline-none focus-visible:outline-none focus-visible:ring-0 active:border-transparent active:!outline-none dark:hover:!outline-none dark:focus:!outline-none"
                  :style="stickyNoteTextareaStyle"
                  rows="3"
                  :placeholder="
                    t('CONVERSATION_WORKFLOW.FLOW_EDITOR.STICKY_PLACEHOLDER')
                  "
                  @mousedown.stop
                  @blur="onStickyTextareaBlur"
                />
                <button
                  type="button"
                  data-flow-sticky-delete
                  class="pointer-events-auto absolute right-1 top-1 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent opacity-0 transition-colors transition-opacity group-hover/sticky:opacity-100"
                  :style="stickyNoteDeleteStyle"
                  :title="t('CONVERSATION_WORKFLOW.FLOW_EDITOR.DELETE_STICKY')"
                  @mouseenter="onStickyDeleteEnter"
                  @mouseleave="onStickyDeleteLeave"
                  @mousedown.stop
                  @click.stop="deleteStickyNote(note)"
                >
                  <span class="i-lucide-x size-3.5 shrink-0" />
                </button>
                <button
                  type="button"
                  data-flow-sticky-resize
                  class="pointer-events-auto absolute bottom-1 right-1 z-10 flex h-5 w-5 cursor-nwse-resize items-center justify-center rounded-br-md border-0 bg-transparent p-0 opacity-60 transition-opacity hover:opacity-100"
                  :style="{ color: STICKY_HEX.iconStroke }"
                  :title="t('CONVERSATION_WORKFLOW.FLOW_EDITOR.STICKY_RESIZE')"
                  @mousedown.stop="onStickyResizeStart(note, $event)"
                >
                  <span
                    class="i-lucide-move-diagonal-2 pointer-events-none size-3 shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
            <svg
              class="absolute z-[1] overflow-visible pointer-events-none text-n-slate-8"
              :style="{
                left: '0',
                top: '0',
                width: '4000px',
                height: '4000px',
              }"
            >
              <defs>
                <marker
                  :id="markerId"
                  markerWidth="8"
                  markerHeight="8"
                  refX="6"
                  refY="4"
                  orient="auto"
                >
                  <path d="M0,0 L8,4 L0,8 L2,4 z" class="fill-current" />
                </marker>
              </defs>
              <path
                v-for="edge in graph.edges"
                :key="edge.id"
                :d="edgePath(edge)"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                :marker-end="`url(#${markerId})`"
              />
            </svg>

            <div
              v-for="edge in graph.edges"
              :key="`edge-del-${edge.id}`"
              data-flow-edge-delete
              class="group/edge pointer-events-auto absolute z-[2] flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
              :style="
                edgeMidpoint(edge)
                  ? {
                      left: `${edgeMidpoint(edge).x}px`,
                      top: `${edgeMidpoint(edge).y}px`,
                    }
                  : { display: 'none' }
              "
            >
              <button
                v-if="edgeMidpoint(edge)"
                type="button"
                title="Excluir conexão"
                class="flex h-7 w-7 items-center justify-center rounded-full border border-n-weak bg-n-solid-1 text-n-slate-11 shadow-md opacity-0 pointer-events-none transition-opacity hover:border-n-ruby-7 hover:bg-n-ruby-3 hover:text-n-ruby-11 group-hover/edge:opacity-100 group-hover/edge:pointer-events-auto"
                @mousedown.stop
                @click.stop="deleteEdge(edge)"
              >
                <span class="i-lucide-trash-2 size-3.5 shrink-0" />
              </button>
            </div>

            <div
              v-for="node in graph.nodes"
              :key="node.id"
              data-flow-node
              class="group absolute z-[1] cursor-pointer shadow-md"
              :class="[
                isAiAgentActionNode(node)
                  ? 'rounded-full border-2 border-violet-400 bg-violet-100 shadow-lg shadow-violet-500/25 dark:border-violet-500 dark:bg-violet-950 dark:shadow-black/30'
                  : 'rounded-lg border border-n-weak bg-n-solid-1',
                isConditionActionNode(node)
                  ? 'w-72 min-w-[18rem]'
                  : isAiAgentActionNode(node)
                    ? ''
                    : 'w-[200px]',
                selectedNodeId === node.id
                  ? 'ring-2 ring-n-teal-9 ring-offset-2 ring-offset-n-alpha-2'
                  : '',
              ]"
              :style="{
                left: `${node.position.x}px`,
                top: `${node.position.y}px`,
                height: `${nodeHeight(node)}px`,
                ...(isAiAgentActionNode(node)
                  ? { width: `${AI_AGENT_NODE_SIZE}px` }
                  : {}),
              }"
              @click="onNodeClick(node)"
            >
              <div
                class="absolute z-30 flex opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto"
                :class="
                  isAiAgentActionNode(node)
                    ? 'left-1/2 bottom-[calc(100%-0.25rem)] -translate-x-1/2 gap-1 rounded-full border border-violet-300/90 bg-n-solid-1 px-1 py-0.5 shadow-lg dark:border-violet-600 dark:bg-violet-900'
                    : 'right-1 top-1 gap-0.5'
                "
              >
                <button
                  type="button"
                  class="flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
                  :class="
                    isAiAgentActionNode(node)
                      ? 'rounded-full hover:bg-violet-500/15 dark:hover:bg-violet-500/25'
                      : ''
                  "
                  @mousedown.stop
                  @click.stop="duplicateNode(node)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-3.5 shrink-0"
                    aria-hidden="true"
                  >
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                    <path
                      d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  title="Excluir nó"
                  class="flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-n-slate-11 transition-colors hover:border-n-ruby-7 hover:bg-n-ruby-3 hover:text-n-ruby-11"
                  :class="isAiAgentActionNode(node) ? 'rounded-full' : ''"
                  @mousedown.stop
                  @click.stop="deleteNode(node)"
                >
                  <span class="i-lucide-trash-2 size-3.5 shrink-0" />
                </button>
              </div>
              <div
                v-if="pinnedExecutionPreviewActive"
                class="pointer-events-none absolute z-[12] flex h-5 w-5 items-center justify-center rounded-full border border-n-teal-8 bg-n-teal-3 shadow-sm ring-1 ring-n-teal-9/20 dark:border-n-teal-7 dark:bg-n-teal-11/25 dark:ring-n-teal-9/30"
                :class="
                  isAiAgentActionNode(node)
                    ? 'bottom-3 right-3'
                    : 'right-2 top-11'
                "
                :title="pinnedExecutionPinTitle"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  class="shrink-0 text-n-teal-11 dark:text-n-teal-1"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 17v5" />
                  <path
                    d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
                  />
                </svg>
              </div>
              <button
                v-if="node.type === 'action'"
                type="button"
                data-flow-input
                :data-node-id="node.id"
                class="absolute z-20 left-0 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full pointer-events-auto"
                title="Entrada — conectar de outro nó"
                @mousedown.stop
                @click.stop
              >
                <span
                  class="block h-3 w-3 shrink-0 rounded-full border-2 border-n-teal-9 bg-n-solid-1 shadow-sm ring-2 ring-n-alpha-2"
                />
              </button>
              <button
                v-if="
                  node.type === 'trigger' ||
                  (node.type === 'action' && node.data?.kind !== 'condition')
                "
                type="button"
                data-flow-output
                :data-node-id="node.id"
                class="absolute z-20 right-0 top-1/2 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full pointer-events-auto"
                title="Saída — arrastar para conectar"
                @mousedown.stop="onOutputPointerDown(node, $event)"
                @click.stop
              >
                <span
                  class="block h-3 w-3 shrink-0 rounded-full border-2 bg-n-solid-1 shadow-sm ring-2 ring-n-alpha-2"
                  :class="node.type === 'trigger' ? '' : 'border-amber-500'"
                  :style="triggerNodeOutputHandleStyle(node)"
                />
              </button>
              <div
                v-if="!isAiAgentActionNode(node)"
                class="flex cursor-grab items-center gap-2 overflow-hidden rounded-t-lg border-b border-n-weak px-3 py-2 active:cursor-grabbing"
                :class="node.type === 'trigger' ? '' : 'bg-n-alpha-2'"
                :style="triggerNodeHeaderStyle(node)"
                @mousedown.stop="onNodeDragStart(node, $event)"
              >
                <span
                  class="size-4 shrink-0"
                  :class="
                    node.type === 'trigger'
                      ? 'i-lucide-zap'
                      : flowActionKindIconClass(node.data?.kind)
                  "
                  :style="triggerNodeIconStyle(node)"
                />
                <span
                  class="text-xs font-semibold uppercase tracking-wide text-n-slate-11 truncate pr-6"
                >
                  {{
                    node.type === 'trigger'
                      ? 'Gatilho'
                      : node.data?.kind === 'condition'
                        ? 'Condição'
                        : 'Ação'
                  }}
                </span>
              </div>
              <div
                v-if="!isAiAgentActionNode(node)"
                class="px-3 py-2 text-sm font-medium text-n-slate-12 leading-snug truncate"
              >
                {{ node.data?.label || kindLabel(node.type, node.data?.kind) }}
              </div>
              <div
                v-else
                class="flex h-full min-h-0 cursor-grab flex-col items-center justify-center gap-1.5 px-2 pb-2 pt-1 text-center active:cursor-grabbing"
                @mousedown.stop="onNodeDragStart(node, $event)"
              >
                <span
                  class="pointer-events-none size-11 shrink-0 text-violet-700 i-lucide-bot dark:text-violet-200"
                />
                <span
                  class="line-clamp-2 min-w-0 max-w-full px-1 text-center text-xs font-semibold leading-tight text-violet-950 dark:text-violet-50"
                >
                  {{
                    node.data?.label || kindLabel(node.type, node.data?.kind)
                  }}
                </span>
              </div>
              <div
                v-if="node.type === 'action' && node.data?.kind === 'condition'"
                class="border-t border-n-weak divide-y divide-n-weak"
              >
                <div
                  v-for="(rule, ruleIdx) in node.data.condition_rules"
                  :key="rule.id"
                  class="relative flex min-h-9 items-center gap-1.5 pr-11 pl-2 py-1.5"
                >
                  <span
                    class="min-w-0 flex-1 text-left text-[10px] font-medium leading-tight text-n-slate-11"
                    :title="conditionRuleSummaryLine(rule)"
                  >
                    <span class="text-n-slate-10">{{ ruleIdx + 1 }}.</span>
                    {{ conditionRuleSummaryLine(rule) }}
                  </span>
                  <button
                    type="button"
                    data-flow-output
                    :data-source-handle="rule.id"
                    :data-node-id="node.id"
                    class="absolute z-20 right-0 top-1/2 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full pointer-events-auto"
                    @mousedown.stop="onOutputPointerDown(node, $event)"
                    @click.stop
                  >
                    <span
                      class="block h-3 w-3 shrink-0 rounded-full border-2 border-emerald-500 bg-n-solid-1 shadow-sm ring-2 ring-n-alpha-2"
                    />
                  </button>
                </div>
                <div
                  class="relative flex min-h-9 items-center gap-1.5 pr-11 pl-2 py-1.5"
                >
                  <span
                    class="min-w-0 flex-1 text-left text-[10px] font-medium leading-tight text-n-slate-11"
                  >
                    SENÃO (nenhuma condição acima)
                  </span>
                  <button
                    type="button"
                    data-flow-output
                    data-source-handle="else"
                    :data-node-id="node.id"
                    class="absolute z-20 right-0 top-1/2 flex h-8 w-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full pointer-events-auto"
                    @mousedown.stop="onOutputPointerDown(node, $event)"
                    @click.stop
                  >
                    <span
                      class="block h-3 w-3 shrink-0 rounded-full border-2 border-n-slate-9 bg-n-solid-1 shadow-sm ring-2 ring-n-alpha-2"
                    />
                  </button>
                </div>
              </div>
              <div
                v-if="actionSendMessageCardSnippet(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Texto </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionSendMessageCardSnippet(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionPrivateNoteCardSnippet(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Nota </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionPrivateNoteCardSnippet(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionAddLabelCardTitle(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Etiqueta </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionAddLabelCardTitle(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionRemoveLabelCardTitle(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Remover </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionRemoveLabelCardTitle(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionAssignAgentCardName(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Agente </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionAssignAgentCardName(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionAssignTeamCardName(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Time </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionAssignTeamCardName(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionRemoveAgentHint(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Agente </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionRemoveAgentHint(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionRemoveTeamCardSummary(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Time </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionRemoveTeamCardSummary(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="summarizeWorkflowConversationCustomAttributes(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Atributos </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ summarizeWorkflowConversationCustomAttributes(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionTransferInboxCardName(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Inbox </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionTransferInboxCardName(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionChangeStatusCardLabel(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Status </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionChangeStatusCardLabel(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="actionChangePriorityCardLabel(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Prioridade </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ actionChangePriorityCardLabel(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="summarizeWorkflowWaitActionMinutes(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Espera </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ summarizeWorkflowWaitActionMinutes(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="summarizeWorkflowHttpRequest(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> HTTP </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                    :title="node.data.http_url ?? ''"
                  >
                    {{ summarizeWorkflowHttpRequest(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="kanbanTriggerCardSummary(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Kanban </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ kanbanTriggerCardSummary(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="kanbanActionCardSummary(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Kanban </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ kanbanActionCardSummary(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="triggerLabelAddedCardTitle(node)"
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div class="flex min-w-0 items-center gap-1.5">
                  <span :class="typeLabelBadgeClass"> Etiqueta </span>
                  <span
                    class="min-w-0 flex-1 truncate text-[11px] font-normal leading-snug text-n-slate-12"
                  >
                    {{ triggerLabelAddedCardTitle(node) }}
                  </span>
                </div>
              </div>
              <div
                v-if="
                  triggerMessageCardTypeLabelsText(node) ||
                  triggerMessageCardContentRow(node) ||
                  triggerInboxCardLabelsText(node)
                "
                class="border-t border-n-weak px-3 pb-2 pt-1.5"
              >
                <div
                  v-if="triggerMessageCardTypeLabelsText(node)"
                  class="flex min-w-0 items-center gap-1.5"
                >
                  <span :class="typeLabelBadgeClass"> Tipo </span>
                  <span
                    class="min-w-0 truncate text-[11px] font-normal leading-tight text-n-slate-12"
                  >
                    {{ triggerMessageCardTypeLabelsText(node) }}
                  </span>
                </div>
                <div
                  v-if="triggerMessageCardContentRow(node)"
                  class="flex min-w-0 items-center gap-1.5"
                  :class="triggerMessageCardTypeLabelsText(node) ? 'mt-1' : ''"
                >
                  <span
                    class="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                    :class="triggerMessageCardContentRow(node).badgeClass"
                  >
                    {{ triggerMessageCardContentRow(node).opLabel }}
                  </span>
                  <span
                    class="min-w-0 truncate text-[11px] leading-tight text-n-slate-11"
                  >
                    {{ triggerMessageCardContentRow(node).snippet }}
                  </span>
                </div>
                <div
                  v-if="triggerInboxCardLabelsText(node)"
                  class="flex min-w-0 items-center gap-1.5"
                  :class="
                    triggerMessageCardTypeLabelsText(node) ||
                    triggerMessageCardContentRow(node)
                      ? 'mt-1'
                      : ''
                  "
                >
                  <span :class="typeLabelBadgeClass"> Caixa </span>
                  <span
                    class="min-w-0 truncate text-[11px] font-normal leading-tight text-n-slate-12"
                  >
                    {{ triggerInboxCardLabelsText(node) }}
                  </span>
                </div>
              </div>
            </div>

            <svg
              v-if="linkDrag.active && ghostLinkPath"
              class="absolute z-[50] overflow-visible pointer-events-none text-teal-500"
              :style="{
                left: '0',
                top: '0',
                width: '4000px',
                height: '4000px',
              }"
            >
              <defs>
                <marker
                  :id="ghostMarkerId"
                  markerWidth="8"
                  markerHeight="8"
                  refX="6"
                  refY="4"
                  orient="auto"
                >
                  <path d="M0,0 L8,4 L0,8 L2,4 z" fill="currentColor" />
                </marker>
              </defs>
              <path
                :d="ghostLinkPath"
                class="text-teal-500"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-dasharray="6 4"
                vector-effect="non-scaling-stroke"
                :marker-end="`url(#${ghostMarkerId})`"
              />
            </svg>
          </div>
        </div>

        <FlowNodeConfigPanel
          v-if="selectedNode"
          v-model:variables-panel-expanded="workflowVariablesPanelExpanded"
          :node="selectedNode"
          :pinned-execution="pinnedExecution"
          :graph-nodes="graph.nodes"
          :graph-edges="graph.edges"
          :default-node-kind-label="
            kindLabel(selectedNode.type, selectedNode.data?.kind)
          "
          @close="closeNodePanel"
          @clear-pinned-execution="onClearPinnedExecution"
          @prune-edges-from-handle="onPruneEdgesFromHandle"
        />
      </div>
    </div>
  </div>
</template>
